    import { germanize, applyGermanize } from "./germanize.js";
    import { strHash, seededShuffle } from "./helpers.js";
    import { parseHash, buildHash } from "./routing.js";
    import { search } from "./search.js";

    const { useState, useEffect } = React;

    // ─── Design Tokens ───────────────────────────────────────────────────────
    const accent       = "#f57669";
    const accentDim    = "rgba(245,118,105,0.12)";
    const accentBorder = "rgba(245,118,105,0.28)";
    const bg           = "#262729";
    const surface      = "#2d2e31";
    const surface2     = "#34363a";
    const border       = "#3c3e42";
    const borderLight  = "#4d5055";
    const textMuted    = "#a8aaae";
    const textDim      = "#9da1a5";
    const warn         = "#e8c97a";
    const danger       = "#e87a7a";

    // ─── Mobile-Hook ─────────────────────────────────────────────────────────
    function useIsMobile() {
      const [mob, setMob] = React.useState(window.innerWidth < 640);
      React.useEffect(() => {
        const h = () => setMob(window.innerWidth < 640);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
      }, []);
      return mob;
    }

    // ─── Persistenz ──────────────────────────────────────────────────────────
    const STORAGE_PREFIX = "secondorder.v1.";
    function usePersistentState(key, initial) {
      const fullKey = STORAGE_PREFIX + key;
      const [v, setV] = useState(() => {
        try {
          const raw = localStorage.getItem(fullKey);
          if (raw === null) return initial;
          return JSON.parse(raw);
        } catch (e) { return initial; }
      });
      useEffect(() => {
        try { localStorage.setItem(fullKey, JSON.stringify(v)); } catch (e) {}
      }, [fullKey, v]);
      return [v, setV];
    }

    // Umlaut-Render-Layer (germanize/applyGermanize) -> src/germanize.js

    // ─── Inhalte aller Themen ────────────────────────────────────────────────

    for (const id of Object.keys(ALL_DATA)) ALL_DATA[id] = applyGermanize(ALL_DATA[id]);

    // Optionen deterministisch mischen — verhindert dass Position 2 immer die passfähigste ist
    // strHash + seededShuffle -> src/helpers.js
    for (const id of Object.keys(ALL_DATA)) {
      const data = ALL_DATA[id];
      if (!Array.isArray(data.scenarios)) continue;
      data.scenarios = data.scenarios.map((sc, si) => {
        if (!Array.isArray(sc.options) || sc.options.length < 4 || !Array.isArray(sc.optionReflections) || sc.optionReflections.length < 4) return sc;
        const order = seededShuffle([0, 1, 2, 3], strHash(id + si));
        return { ...sc, options: order.map(i => sc.options[i]), optionReflections: order.map(i => sc.optionReflections[i]) };
      });
    }

    // Einmaliger Reset des Anwenden-Fortschritts (wegen Shuffle-Einführung)
    if (!localStorage.getItem("secondorder.v1.global.shuffleV1")) {
      Object.keys(localStorage)
        .filter(k => k.startsWith("secondorder.v1.") && k.includes(".anwenden."))
        .forEach(k => localStorage.removeItem(k));
      localStorage.setItem("secondorder.v1.global.shuffleV1", "1");
    }

    const LEARNING_PATHS = [
      {
        id: "einstieg",
        title: "Wo fange ich an?",
        leitfrage: "Vier Stationen, die das Wesentliche von Second Order greifbar machen — Schritt für Schritt.",
        onboarding: true,
        stations: [
          { id: "watzlawick", mode: "entdecken", ueberleitung: "Kommunikation konstruiert Wirklichkeit — Bateson fragt: Welche Muster machen dabei den Unterschied?" },
          { id: "bateson",    mode: "entdecken", ueberleitung: "Der Unterschied, der einen Unterschied macht — Foerster denkt das zur Beobachtung zweiter Ordnung weiter." },
          { id: "foerster",   mode: "entdecken", ueberleitung: "Beobachte den Beobachter — Scharmer fragt: Was bedeutet das fuer unser Handeln?" },
          { id: "scharmer",   mode: "entdecken", ueberleitung: null }
        ]
      },
      {
        id: "wirklichkeit",
        title: "Wie entsteht Wirklichkeit?",
        leitfrage: "Fünf Denker*innen, die zeigen: Wirklichkeit wird konstruiert, nicht entdeckt.",
        onboarding: false,
        stations: [
          { id: "foerster",    mode: "entdecken", ueberleitung: "Foersters Beobachter zweiter Ordnung findet bei Maturana seinen biologischen Ursprung." },
          { id: "maturana",    mode: "entdecken", ueberleitung: "Autopoiesis zeigt, wie Systeme sich selbst erschaffen — Glasersfeld fragt: Was koennen wir davon wissen?" },
          { id: "glasersfeld", mode: "entdecken", ueberleitung: "Wenn Wissen Konstruktion ist, zeigt Watzlawick, wie das in der Kommunikation wirkt." },
          { id: "watzlawick",  mode: "vernetzen", ueberleitung: "Kommunikation als Wirklichkeitskonstruktion — Piaget fragt, wie diese Konstruktion in der Entwicklung entsteht." },
          { id: "piaget",      mode: "entdecken", ueberleitung: null }
        ]
      },
      {
        id: "veraenderung",
        title: "Wie funktioniert Lernen?",
        leitfrage: "Von der Doppelschleife bis zum Presencing — wie Lernen in Organisationen wirklich gelingt.",
        onboarding: false,
        stations: [
          { id: "argyris",  mode: "entdecken", ueberleitung: "Argyris unterscheidet Einzel- und Doppelschleife — Senge denkt das zur lernenden Organisation weiter." },
          { id: "senge",    mode: "entdecken", ueberleitung: "Systemdenken braucht Richtung — Scharmer fragt: Aus welcher Zukunft heraus fuehren wir?" },
          { id: "scharmer", mode: "vernetzen", ueberleitung: "Presencing als Bewegung — Dewey hat das Lernen aus Erfahrung grundgelegt." },
          { id: "dewey",    mode: "entdecken", ueberleitung: "Lernen durch Erfahrung in Organisationen — Wimmer denkt das systemisch weiter." },
          { id: "wimmer",   mode: "entdecken", ueberleitung: null }
        ]
      },
      {
        id: "beobachtung",
        title: "Wer beobachtet den Beobachter?",
        leitfrage: "Der Kern-Pfad von Second Order — von der Unterscheidung über Systemtheorie bis zur sozialen Inszenierung.",
        onboarding: false,
        stations: [
          { id: "foerster",     mode: "vernetzen", ueberleitung: "Foersters Beobachtung zweiter Ordnung findet in Luhmanns Systemtheorie ihre gesellschaftliche Reichweite." },
          { id: "luhmann",      mode: "entdecken", ueberleitung: "Systeme beobachten sich selbst — Spencer-Brown hat die Logik dafuer entworfen." },
          { id: "spencerbrown", mode: "entdecken", ueberleitung: "Die Unterscheidung als Grundoperation — Bateson zeigt, was Unterschiede in lebendigen Systemen bewirken." },
          { id: "bateson",      mode: "vernetzen", ueberleitung: "Muster, die Unterschiede machen — Goffman beobachtet, wie wir das in sozialen Interaktionen auffuehren." },
          { id: "goffman",      mode: "entdecken", ueberleitung: null }
        ]
      },
      {
        id: "sprache",
        title: "Was macht Sprache mit uns?",
        leitfrage: "Wie Sprache Wirklichkeit erzeugt, Macht verteilt und Dialog ermöglicht.",
        onboarding: false,
        stations: [
          { id: "wittgenstein", mode: "entdecken", ueberleitung: "Sprachspiele als Wirklichkeit — Watzlawick zeigt, wie das in der Kommunikation funktioniert." },
          { id: "watzlawick",   mode: "entdecken", ueberleitung: "Wenn Kommunikation Wirklichkeit konstruiert, fragt Habermas: Wie koennen wir dennoch zu Verstaendigung kommen?" },
          { id: "habermas",     mode: "entdecken", ueberleitung: "Verstaendigungsorientiertes Handeln — Foucault zeigt, dass Sprache immer auch Macht ist." },
          { id: "foucault",     mode: "entdecken", ueberleitung: "Macht und Wissen sind untrennbar — Derrida fragt, ob Bedeutung selbst je zur Ruhe kommt." },
          { id: "derrida",      mode: "entdecken", ueberleitung: null }
        ]
      },
      {
        id: "psyche",
        title: "Wie verändert sich ein Mensch?",
        leitfrage: "Von der Tiefenpsychologie bis zur Lösungsorientierung — die Psyche als lebendes System.",
        onboarding: false,
        stations: [
          { id: "rogers",    mode: "entdecken", ueberleitung: "Bedingungslose Akzeptanz als Basis — Jung fragt nach den tieferen Schichten, die sich veraendern." },
          { id: "jung",      mode: "entdecken", ueberleitung: "Individuation als lebenslanger Prozess — Berne zeigt, wie Skriptmuster diesen Prozess praegen." },
          { id: "berne",     mode: "vernetzen", ueberleitung: "Psychologische Spiele und Skripte — Laing fragt, was als 'normal' gilt und was das bedeutet." },
          { id: "laing",     mode: "entdecken", ueberleitung: "Das Normale hinterfragen — Winnicott zeigt, wie fruehe Beziehungen die Grundlage legen." },
          { id: "winnicott", mode: "entdecken", ueberleitung: null }
        ]
      }
    ];

    const TOPIC_ORDER = ["kant", "husserl", "wittgenstein", "wiener", "guenther", "ashby", "bateson", "foerster", "pask", "piaget", "glasersfeld", "varela", "vygotsky", "rogers", "laing", "winnicott", "berne", "erickson", "watzlawick", "spencerbrown", "luhmann", "parsons", "maturana", "stierlin", "simon", "satir", "deshazerberg", "gschmidt", "bschmid", "eidenschink", "peyn", "beer", "habermas", "freire", "arendt", "foucault", "derrida", "goffman", "rosa", "senge", "dewey", "scharmer", "jung", "wimmer", "kruse", "argyris", "cohn", "bertalanffy", "meadows", "hegel"].filter(id => ALL_DATA[id]);
    const TOPICS = TOPIC_ORDER.map(id => {
      const d = ALL_DATA[id];
      return { id, label: d.label, title: d.title, em: d.titleEm, subtitle: d.subtitle };
    });

    const TOPIC_GROUPS = [
      { id: "systeme",    label: "Systeme & Steuerung",      ids: ["bertalanffy","wiener","ashby","beer","pask","luhmann","parsons","peyn","bateson","meadows"] },
      { id: "erkenntnis", label: "Erkenntnis & Konstruktion", ids: ["kant","husserl","piaget","vygotsky","glasersfeld","foerster","maturana","varela","spencerbrown","guenther"] },
      { id: "sprache",    label: "Sprache & Gesellschaft",    ids: ["wittgenstein","hegel","watzlawick","habermas","freire","arendt","foucault","derrida","goffman","rosa"] },
      { id: "psyche",     label: "Psyche & Veränderung",      ids: ["jung","berne","rogers","laing","winnicott","erickson","satir","cohn","stierlin","deshazerberg"] },
      { id: "wirkung",    label: "Wirkung & Gestaltung",      ids: ["argyris","senge","dewey","scharmer","wimmer","kruse","simon","eidenschink","gschmidt","bschmid"] },
    ].map(g => ({ ...g, ids: g.ids.filter(id => ALL_DATA[id]) }));

    const THINKER_HEADLINES = {
      bertalanffy:   "Das Ganze ist mehr als seine Teile",
      wiener:        "Steuerung durch Rückkopplung",
      ashby:         "Nur Vielfalt beherrscht Vielfalt",
      beer:          "Lebensfähigkeit als Systemziel",
      pask:          "Lernen ist ein Gespräch",
      luhmann:       "Systeme beobachten sich selbst",
      parsons:       "Gesellschaft funktioniert in vier Modi",
      peyn:          "Systemik als professionelle Haltung",
      bateson:       "Der Unterschied, der einen Unterschied macht",
      meadows:       "Systeme haben Hebelpunkte",
      kant:          "Erkenntnis formt, was sie erkennt",
      husserl:       "Zu den Sachen selbst",
      piaget:        "Wissen entsteht im Handeln",
      vygotsky:      "Lernen geschieht zwischen Menschen",
      glasersfeld:   "Wissen ist Konstruktion, nicht Abbild",
      foerster:      "Beobachte den Beobachter",
      maturana:      "Wir erschaffen, was wir erkennen",
      varela:        "Erkenntnis ist Aktion",
      spencerbrown:  "Triff eine Unterscheidung",
      guenther:      "Es gibt mehr als Ja und Nein",
      wittgenstein:  "Grenzen der Sprache sind Grenzen der Welt",
      hegel:         "Das Wahre ist das Ganze",
      watzlawick:    "Man kann nicht nicht kommunizieren",
      habermas:      "Verständigung als Ziel des Handelns",
      freire:        "Niemand befreit den anderen allein",
      arendt:        "Handeln beginnt immer etwas Neues",
      foucault:      "Wissen ist Macht",
      derrida:       "Bedeutung verschiebt sich endlos",
      goffman:       "Das Leben ist eine Bühne",
      rosa:          "Was das Leben gut macht, laesst sich nicht machen",
      jung:          "Das Unbewusste formt das Bewusste",
      berne:         "Menschen spielen Spiele",
      rogers:        "Der Mensch trägt die Lösung in sich",
      laing:         "Was wir normal nennen, ist oft das Problem",
      winnicott:     "Entwicklung braucht einen haltenden Raum",
      erickson:      "Jeder hat seine eigene Sprache",
      satir:         "In jeder Familie steckt die Welt",
      cohn:          "Das Ich im Wir nicht vergessen",
      stierlin:      "Familien delegieren unbewusst",
      deshazerberg:  "Auf Lösungen fokussieren, nicht auf Probleme",
      argyris:       "Das Lernen lernen",
      senge:         "Systemdenken verbindet alles",
      dewey:         "Lernen heißt Erfahrung reflektieren",
      scharmer:      "Führe aus der Zukunft heraus",
      wimmer:        "Unsteuerbar — und gestaltbar",
      kruse:         "Komplexität braucht Resonanz, nicht Kontrolle",
      simon:         "Probleme sind Lösungen von gestern",
      eidenschink:   "Beratung stört, damit etwas entsteht",
      gschmidt:      "Ressourcen wecken statt Defizite behandeln",
      bschmid:       "Professionalität braucht Rollenklarheit",
    };

    function buildSearchIndex() {
      const thinkers = TOPIC_ORDER.map(id => {
        const d = ALL_DATA[id];
        return { id, label: d.label, title: d.title };
      });
      const terms = TOPIC_ORDER.flatMap(id => {
        const d = ALL_DATA[id];
        return (d.cards || []).map(c => ({ id, term: c.term, thinkerLabel: d.label }));
      });
      return { thinkers, terms };
    }

    // search() -> src/search.js

    // ─── Komponenten ─────────────────────────────────────────────────────────

    const FlipCard = React.memo(function FlipCard({ card, seen, onFirstFlip, highlight }) {
      const [flipped, setFlipped] = useState(false);
      const [expanded, setExpanded] = useState(false);
      const hasMehr = card.beispiel || (card.erkennungsmerkmale && card.erkennungsmerkmale.length > 0);
      function handle() {
        const next = !flipped;
        setFlipped(next);
        if (!next) setExpanded(false);
        if (!seen && next) { onFirstFlip && onFirstFlip(); }
      }
      function handleMehr(e) {
        e.stopPropagation();
        setExpanded(x => !x);
      }
      return (
        <button type="button" data-term={card.term} onClick={handle} aria-pressed={flipped} aria-label={(flipped ? "Karte schließen: " : "Karte öffnen: ") + card.term}
          style={{ cursor: "pointer", position: "relative", background: "none", border: 0, padding: 0, textAlign: "left", width: "100%", font: "inherit", color: "inherit", display: "block", borderRadius: 12, boxShadow: highlight ? "0 0 0 2px " + accent + ", 0 0 16px " + accentDim : "none", transition: "box-shadow 0.6s ease" }}>
          {seen && <div style={{ position: "absolute", top: 10, right: 10, zIndex: 10, width: 7, height: 7, borderRadius: "50%", background: accent, pointerEvents: "none" }} />}
          {!flipped ? (
            <div style={{ borderRadius: 12, border: "1px solid " + border, background: surface, padding: "20px 18px", display: "flex", flexDirection: "column", gap: 14, minHeight: 120 }}>
              <div style={{ fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: textDim }}>Begriff</div>
              <div style={{ fontFamily: "Georgia, serif", fontSize: "1.3rem", lineHeight: 1.2, color: "#e8ede9", flex: 1 }}>{card.term}</div>
              <div style={{ fontSize: "0.65rem", color: textDim }}>Karte öffnen</div>
            </div>
          ) : (
            <div style={{ borderRadius: 12, border: "1px solid " + accentBorder, background: surface2, padding: "18px", display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: accent }}>{card.term}</div>
              <div style={{ fontSize: "0.77rem", lineHeight: 1.7, color: "#e8ede9" }}>{card.definition}</div>
              <div style={{ borderTop: "1px solid " + border, paddingTop: 10, fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "0.82rem", color: "#f5a99e", lineHeight: 1.55 }}>{card.metapher}</div>
              {hasMehr && (
                <button type="button" onClick={handleMehr}
                  style={{ alignSelf: "flex-start", background: accentDim, border: "1px solid " + accentBorder, borderRadius: 100, padding: "1px 8px", fontSize: "0.6rem", color: accent, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.5 }}>
                  {expanded ? "weniger" : "mehr"}
                </button>
              )}
              {expanded && card.beispiel && (
                <div style={{ paddingLeft: 10, borderLeft: "2px solid " + borderLight }}>
                  <span style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.6rem", display: "block", marginBottom: 4, color: textMuted }}>Beispiel</span>
                  <div style={{ fontFamily: "Georgia, serif", fontSize: "0.77rem", lineHeight: 1.65, color: "#e8ede9" }}>{card.beispiel}</div>
                </div>
              )}
              {expanded && card.erkennungsmerkmale && card.erkennungsmerkmale.length > 0 && (
                <div>
                  <span style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.6rem", display: "block", marginBottom: 6, color: textMuted }}>Erkennungsmerkmale</span>
                  <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
                    {card.erkennungsmerkmale.map((m, i) => (
                      <li key={i} style={{ fontSize: "0.72rem", color: textMuted, lineHeight: 1.55, paddingLeft: 12, position: "relative" }}>
                        <span style={{ position: "absolute", left: 0, color: accent }}>·</span>{m}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {card.abgrenzung && (
                <div style={{ borderTop: "1px solid " + border, paddingTop: 8, paddingLeft: 10, borderLeft: "2px solid " + accent, fontSize: "0.7rem", color: textMuted, lineHeight: 1.65 }}>
                  <span style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.6rem", display: "block", marginBottom: 3, color: accent }}>Nicht zu verwechseln mit</span>
                  {card.abgrenzung}
                </div>
              )}
              <div style={{ borderTop: "1px solid " + border, paddingTop: 7, fontSize: "0.65rem", color: textDim, lineHeight: 1.6 }}>
                <span style={{ textTransform: "uppercase", letterSpacing: "0.05em", color: accent }}>Quellen: </span>
                {(card.quellen || []).join(" / ")}
              </div>
              <div style={{ fontSize: "0.65rem", color: textDim, textAlign: "right" }}>Karte schliessen</div>
            </div>
          )}
        </button>
      );
    });

    function EntdeckenPanel({ data, scrollToTerm }) {
      const cards = data.cards || [];
      const [seenArr, setSeenArr] = usePersistentState(data.id + ".entdecken.seen", []);
      const seenSet = new Set(seenArr);
      const seenCount = seenSet.size;
      const [hlTerm, setHlTerm] = React.useState(null);
      const [showTerms, setShowTerms] = React.useState(true);
      const kbIdxRef = React.useRef(-1);
      const touchStartRef = React.useRef(null);

      React.useEffect(() => {
        function onTouchStart(e) { touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }
        function onTouchEnd(e) {
          if (!touchStartRef.current) return;
          const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
          const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
          touchStartRef.current = null;
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
          const dir = dx < 0 ? "right" : "left";
          const cur = kbIdxRef.current;
          const next = dir === "right" ? Math.min((cur < 0 ? -1 : cur) + 1, cards.length - 1) : Math.max(cur <= 0 ? 0 : cur - 1, 0);
          if (next < 0) return;
          kbIdxRef.current = next;
          const card = cards[next];
          if (!card) return;
          setHlTerm(card.term);
          const el = Array.from(document.querySelectorAll("[data-term]")).find(el => el.getAttribute("data-term") === card.term);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            if (el.getAttribute("aria-pressed") === "false") setTimeout(() => el.click(), 1250);
          }
          setTimeout(() => setHlTerm(null), 1400);
        }
        document.addEventListener("touchstart", onTouchStart, { passive: true });
        document.addEventListener("touchend", onTouchEnd, { passive: true });
        return () => { document.removeEventListener("touchstart", onTouchStart); document.removeEventListener("touchend", onTouchEnd); };
      }, [cards]);

      React.useEffect(() => {
        function onKey(e) {
          if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
          if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
            e.preventDefault();
            const cur = kbIdxRef.current;
            const next = e.key === "ArrowRight"
              ? Math.min((cur < 0 ? -1 : cur) + 1, cards.length - 1)
              : Math.max(cur <= 0 ? 0 : cur - 1, 0);
            if (next < 0) return;
            kbIdxRef.current = next;
            const card = cards[next];
            if (!card) return;
            setHlTerm(card.term);
            const el = Array.from(document.querySelectorAll("[data-term]")).find(el => el.getAttribute("data-term") === card.term);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => setHlTerm(null), 1200);
          }
          if ((e.key === " " || e.key === "Enter") && kbIdxRef.current >= 0) {
            e.preventDefault();
            const card = cards[kbIdxRef.current];
            if (!card) return;
            const el = Array.from(document.querySelectorAll("[data-term]")).find(el => el.getAttribute("data-term") === card.term);
            if (el) el.click();
          }
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
      }, [cards]);

      function scrollToCard(term) {
        setHlTerm(term);
        setTimeout(() => {
          const el = Array.from(document.querySelectorAll("[data-term]")).find(e => e.getAttribute("data-term") === term);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            if (el.getAttribute("aria-pressed") === "false") setTimeout(() => el.click(), 1250);
          }
        }, 50);
        setTimeout(() => setHlTerm(null), 1400);
      }
      React.useEffect(() => {
        if (!scrollToTerm) return;
        const term = scrollToTerm.term;
        setHlTerm(term);
        const t1 = setTimeout(() => {
          const el = Array.from(document.querySelectorAll('[data-term]')).find(e => e.getAttribute('data-term') === term);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 350);
        const t2 = setTimeout(() => {
          const el = Array.from(document.querySelectorAll('[data-term]')).find(e => e.getAttribute('data-term') === term);
          if (el && el.getAttribute("aria-pressed") === "false") el.click();
        }, 350 + 1250);
        const t3 = setTimeout(() => setHlTerm(null), 350 + 1250 + 400);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
      }, [scrollToTerm]);
      function markSeen(i) {
        if (seenSet.has(i)) return;
        setSeenArr([...seenArr, i]);
      }
      return (
        <div>
          <div style={{ height: 2, background: border, borderRadius: 2, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ height: "100%", background: accent, width: (cards.length ? (seenCount / cards.length) * 100 : 0) + "%", transition: "width 0.4s", borderRadius: 2 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "13px 16px", background: surface, border: "1px solid " + border, borderRadius: 12, marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: "Georgia, serif", fontSize: "1.7rem", color: accent, lineHeight: 1 }}>{seenCount}<span style={{ fontSize: "0.9rem", color: textDim }}> / {cards.length}</span></div>
              <div style={{ fontSize: "0.65rem", color: textDim }}>Begriffe entdeckt</div>
            </div>
            <div style={{ width: 1, height: 26, background: border }} />
            <div style={{ fontSize: "0.7rem", color: textMuted, lineHeight: 1.6 }}>Was du betrachtest –<br />wird sichtbar.</div>
          </div>
          {/* Begriffe-Übersicht */}
          <div style={{ marginBottom: 14 }}>
            <button onClick={() => setShowTerms(o => !o)}
              style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: 0, fontFamily: "inherit", width: "100%" }}>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, transform: showTerms ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                <path d="M2 4l4 4 4-4" stroke={textDim} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ fontSize: "0.65rem", color: textDim, letterSpacing: "0.06em" }}>
                {showTerms ? "Begriffe ausblenden" : "Alle " + cards.length + " Begriffe"}
              </span>
            </button>
            {showTerms && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                {cards.map((card, i) => {
                  const seen = seenSet.has(i);
                  return (
                    <button key={i} onClick={() => scrollToCard(card.term)}
                      style={{ background: seen ? accentDim : "transparent", border: "1px solid " + (seen ? accentBorder : borderLight), borderRadius: 20, padding: "3px 10px", color: seen ? accent : textDim, fontSize: "0.65rem", fontFamily: "inherit", cursor: "pointer" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = accentBorder; e.currentTarget.style.color = accent; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = seen ? accentBorder : borderLight; e.currentTarget.style.color = seen ? accent : textDim; }}>
                      {card.term}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
            {cards.map((card, i) => <FlipCard key={i} card={card} seen={seenSet.has(i)} onFirstFlip={() => markSeen(i)} highlight={hlTerm === card.term} />)}
          </div>
        </div>
      );
    }

    function VernetzenPanel({ data, onGoAnwenden }) {
      const quiz = data.quiz || [];
      const relations = data.relations || {};
      const [active, setActive] = useState(null);
      const [qIdx, setQIdx] = usePersistentState(data.id + ".vernetzen.qIdx", 0);
      const [answers, setAnswers] = usePersistentState(data.id + ".vernetzen.answers", {});
      const [revealed, setRevealed] = usePersistentState(data.id + ".vernetzen.revealed", {});
      const [exploredArr, setExploredArr] = usePersistentState(data.id + ".vernetzen.explored", []);
      const exploredSet = new Set(exploredArr);
      const totalTerms = Object.keys(relations).length;
      const situationsExplored = quiz.reduce((n, qq, i) => {
        const a = answers[i];
        const resolvedI = (a !== undefined && a === qq.correct) || revealed[i] === true;
        return n + (resolvedI ? 1 : 0);
      }, 0);
      function openTerm(term) {
        setActive(active === term ? null : term);
        if (!exploredSet.has(term)) setExploredArr([...exploredArr, term]);
      }
      const safeQIdx = Math.min(qIdx, Math.max(0, quiz.length - 1));
      const q = quiz[safeQIdx];
      const rels = active ? (relations[active] || []) : [];
      const selected = answers[safeQIdx];
      const hasAnswered = selected !== undefined;
      const isCorrect = hasAnswered && selected === q?.correct;
      const isRevealed = revealed[safeQIdx] === true;
      const resolved = isCorrect || isRevealed;

      function chooseAnswer(i) {
        if (resolved) return;
        setAnswers({ ...answers, [safeQIdx]: i });
      }

      React.useEffect(() => {
        function onKey(e) {
          if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
          if (e.key === "ArrowRight") { e.preventDefault(); setQIdx(i => Math.min(i + 1, quiz.length - 1)); }
          if (e.key === "ArrowLeft")  { e.preventDefault(); setQIdx(i => Math.max(i - 1, 0)); }
          if (["1","2","3","4"].includes(e.key) && !resolved && selected === undefined) {
            setAnswers(prev => ({ ...prev, [safeQIdx]: parseInt(e.key) - 1 }));
          }
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
      }, [quiz.length, resolved, selected, safeQIdx]);

      React.useEffect(() => {
        let sx = 0, sy = 0;
        function onTouchStart(e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }
        function onTouchEnd(e) {
          const dx = e.changedTouches[0].clientX - sx;
          const dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
          if (dx < 0) setQIdx(i => Math.min(i + 1, quiz.length - 1));
          else setQIdx(i => Math.max(i - 1, 0));
        }
        document.addEventListener("touchstart", onTouchStart, { passive: true });
        document.addEventListener("touchend", onTouchEnd, { passive: true });
        return () => { document.removeEventListener("touchstart", onTouchStart); document.removeEventListener("touchend", onTouchEnd); };
      }, [quiz.length]);

      function retry() {
        const a = { ...answers }; delete a[safeQIdx]; setAnswers(a);
      }
      function reveal() {
        setRevealed({ ...revealed, [safeQIdx]: true });
      }
      function nextQuestion() {
        if (safeQIdx < quiz.length - 1) setQIdx(safeQIdx + 1);
      }

      if (!q) return <div style={{ color: textDim, fontSize: "0.8rem" }}>Keine Quiz-Daten verfuegbar.</div>;

      return (
        <div>
          <div style={{ height: 2, background: border, borderRadius: 2, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ height: "100%", background: accent, width: (quiz.length ? (situationsExplored / quiz.length) * 100 : 0) + "%", transition: "width 0.4s", borderRadius: 2 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "13px 16px", background: surface, border: "1px solid " + border, borderRadius: 12, marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: "Georgia, serif", fontSize: "1.7rem", color: accent, lineHeight: 1 }}>{situationsExplored}<span style={{ fontSize: "0.9rem", color: textDim }}> / {quiz.length}</span></div>
              <div style={{ fontSize: "0.65rem", color: textDim }}>Situationen erkundet</div>
            </div>
            <div style={{ width: 1, height: 26, background: border }} />
            <div style={{ fontSize: "0.7rem", color: textMuted, lineHeight: 1.6 }}>Was du verbindest –<br />wird greifbar.</div>
          </div>
          <div style={{ background: surface, border: "1px solid " + border, borderRadius: 12, padding: 20, marginBottom: 14 }}>
            <div style={{ fontSize: "0.65rem", letterSpacing: "0.08em", textTransform: "uppercase", color: textMuted, marginBottom: 12 }}>Situation {safeQIdx + 1} von {quiz.length}</div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "1.1rem", lineHeight: 1.45, color: "#e8ede9", marginBottom: 18 }}>{q.q}</div>
            <div style={{ display: "grid", gap: 7 }}>
              {(q.options || []).map((opt, i) => {
                const optIsCorrect = i === q.correct;
                const isSelected = selected === i;
                const showCorrectMarker = resolved && optIsCorrect;
                let borderColor = border;
                let textOpacity = 1;
                if (resolved) {
                  if (optIsCorrect) borderColor = accent;
                  if (isSelected && !optIsCorrect) textOpacity = 0.5;
                } else if (isSelected) {
                  borderColor = accent;
                }
                return (
                  <button key={i} onClick={() => chooseAnswer(i)} disabled={resolved}
                    style={{ position: "relative", background: surface2, border: "1px solid " + borderColor, borderRadius: 8, padding: "10px 13px", textAlign: "left", color: "#e8ede9", fontSize: "0.72rem", cursor: resolved ? "default" : "pointer", lineHeight: 1.5, display: "block", width: "100%", fontFamily: "inherit", opacity: textOpacity }}>
                    <div style={{ display: "flex", gap: 10 }}>
                      <span style={{ fontSize: "0.65rem", color: textDim, flexShrink: 0, width: 14, marginTop: 1 }}>{["A","B","C","D"][i]}</span>
                      <span style={{ flex: 1 }}>
                        {opt}
                        {showCorrectMarker && (
                          <span style={{ display: "block", fontSize: "0.65rem", color: textDim, marginTop: 4, fontStyle: "italic" }}>passfähigste Perspektive</span>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            {hasAnswered && !resolved && (
              <div style={{ marginTop: 14, padding: "12px 14px", background: surface2, border: "1px solid " + border, borderRadius: 8, fontSize: "0.7rem", color: textMuted, lineHeight: 1.65 }}>
                <span style={{ color: accent, fontWeight: 600 }}>Hmm,</span>{" "}da reibt sich etwas.
              </div>
            )}
            {resolved && (
              <div style={{ marginTop: 14, padding: "12px 14px", background: surface2, border: "1px solid " + border, borderRadius: 8, fontSize: "0.7rem", color: textMuted, lineHeight: 1.65 }}>
                <span style={{ color: accent, fontWeight: 600 }}>Das funktioniert.</span> {q.right || ""}
              </div>
            )}
            <div aria-live="polite" aria-atomic="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
              {hasAnswered && !resolved ? "Hmm, da reibt sich etwas." : resolved ? "Das funktioniert. " + (q.right || "") : ""}
            </div>
            {hasAnswered && !resolved && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                <button onClick={retry}
                  style={{ background: surface2, border: "1px solid " + border, color: "#e8ede9", padding: "6px 14px", borderRadius: 100, fontSize: "0.68rem", cursor: "pointer" }}>
                  Nochmal
                </button>
                <button onClick={reveal}
                  style={{ background: accentDim, border: "1px solid " + accent, color: accent, padding: "6px 14px", borderRadius: 100, fontSize: "0.68rem", cursor: "pointer" }}>
                  Auflösung zeigen
                </button>
              </div>
            )}
            {resolved && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                {safeQIdx < quiz.length - 1 ? (
                  <button onClick={nextQuestion}
                    style={{ background: accentDim, border: "1px solid " + accent, color: accent, padding: "6px 16px", borderRadius: 100, fontSize: "0.68rem", cursor: "pointer" }}>
                    Nächste Situation →
                  </button>
                ) : (
                  <button onClick={() => onGoAnwenden && onGoAnwenden()}
                    style={{ background: accentDim, border: "1px solid " + accent, color: accent, padding: "6px 16px", borderRadius: 100, fontSize: "0.68rem", cursor: "pointer", lineHeight: 1.5, textAlign: "right" }}>
                    Weiter zum Transfer → Anwenden
                  </button>
                )}
              </div>
            )}
          </div>

          <div style={{ background: surface, border: "1px solid " + border, borderRadius: 12, padding: 18, marginTop: 14 }}>
            <div style={{ fontSize: "0.65rem", letterSpacing: "0.08em", textTransform: "uppercase", color: textDim, marginBottom: 8 }}>Begriffsbeziehungen</div>
            <div style={{ fontSize: "0.72rem", color: textMuted, marginBottom: 10 }}>Welche Verbindung möchtest du genauer betrachten?</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {Object.keys(relations).map(term => (
                <button key={term} onClick={() => openTerm(term)}
                  style={{ background: active === term ? accentDim : surface2, border: "1px solid " + (active === term ? accent : border), color: active === term ? accent : (exploredSet.has(term) ? "#e8ede9" : textMuted), borderRadius: 100, padding: "4px 11px", fontSize: "0.66rem", cursor: "pointer" }}>
                  {germanize(term)}
                </button>
              ))}
            </div>
            {rels.length > 0 ? rels.map((r, i) => (
              <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "7px 0", borderBottom: "1px solid " + border, alignItems: "flex-start" }}>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "0.84rem", color: "#f5a99e" }}>{germanize(active)}</span>
                <span style={{ color: accent, fontSize: "0.65rem", marginTop: 2 }}>{"→ " + r.rel + " →"}</span>
                <span style={{ fontFamily: "Georgia, serif", fontSize: "0.84rem", color: "#f5a99e" }}>{r.b}</span>
                <span style={{ color: textDim, fontSize: "0.65rem", marginTop: 2 }}>{"· " + r.desc}</span>
              </div>
            )) : null}
          </div>

        </div>
      );
    }

    // ─── Notes-Sanitization ───────────────────────────────────────────────────
    function sanitizeNoteHtml(html) {
      const ALLOWED = new Set(["B","I","U","UL","OL","LI","BR","P"]);
      function clean(node) {
        if (node.nodeType === 3) return node.cloneNode(); // text node
        if (node.nodeType !== 1) return null;             // not element
        if (!ALLOWED.has(node.tagName)) {
          const f = document.createDocumentFragment();
          node.childNodes.forEach(c => { const r = clean(c); if (r) f.appendChild(r); });
          return f;
        }
        const el = document.createElement(node.tagName);
        node.childNodes.forEach(c => { const r = clean(c); if (r) el.appendChild(r); });
        return el;
      }
      try {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const out = document.createElement("div");
        doc.body.childNodes.forEach(c => { const r = clean(c); if (r) out.appendChild(r); });
        return out.innerHTML;
      } catch { return ""; }
    }

    // ─── ThinkerNote ──────────────────────────────────────────────────────────
    function ThinkerNote({ topicId }) {
      const [note, setNote] = usePersistentState(topicId + ".note", "");
      const [open, setOpen] = React.useState(false);
      const editorRef = React.useRef(null);

      React.useEffect(() => {
        if (open && editorRef.current && editorRef.current.innerHTML !== note) {
          editorRef.current.innerHTML = sanitizeNoteHtml(note || "");
        }
      }, [open]);

      function exec(cmd) {
        document.execCommand(cmd, false, null);
        editorRef.current.focus();
      }

      function ToolBtn({ label, cmd, style }) {
        return (
          <button
            onMouseDown={e => { e.preventDefault(); exec(cmd); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: textDim, padding: "3px 7px", fontSize: "0.72rem", fontFamily: "inherit", borderRadius: 4, lineHeight: 1, ...style }}
            onMouseEnter={e => e.currentTarget.style.color = "#e8e6e3"}
            onMouseLeave={e => e.currentTarget.style.color = textDim}
          >{label}</button>
        );
      }

      const hasNote = note && note.replace(/<[^>]*>/g, "").trim().length > 0;

      return (
        <div style={{ marginTop: 16 }}>
          <button onClick={() => setOpen(o => !o)}
            style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: 0, fontFamily: "inherit" }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M11 2l3 3-9 9H2v-3L11 2z" stroke={hasNote ? accent : textDim} strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: "0.7rem", color: hasNote ? accent : textDim }}>
              Notizen
            </span>
          </button>
          {open && (
            <div style={{ marginTop: 8, border: "1px solid " + border, borderRadius: 8, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 1, padding: "4px 8px", borderBottom: "1px solid " + border, background: surface2 }}>
                <ToolBtn label="B" cmd="bold" style={{ fontWeight: 700 }} />
                <ToolBtn label="I" cmd="italic" style={{ fontStyle: "italic" }} />
                <ToolBtn label="U" cmd="underline" style={{ textDecoration: "underline" }} />
                <div style={{ width: 1, height: 14, background: border, margin: "0 4px" }} />
                <ToolBtn label="≡" cmd="insertUnorderedList" />
                <ToolBtn label="1." cmd="insertOrderedList" />
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onBlur={() => setNote(sanitizeNoteHtml(editorRef.current.innerHTML))}
                onInput={() => setNote(editorRef.current.innerHTML)}
                placeholder="Deine Gedanken..."
                style={{ minHeight: 80, padding: "10px 14px", outline: "none", fontSize: "0.78rem", color: "#e8e6e3", lineHeight: 1.75, background: bg, fontFamily: "'Raleway', system-ui, sans-serif", caretColor: accent }}
              />
            </div>
          )}
        </div>
      );
    }

    function getNextPathStation(topicId) {
      const allPaths = [...LEARNING_PATHS, ...(JSON.parse(localStorage.getItem(STORAGE_PREFIX + "global.pfade") || "[]"))];
      const path = allPaths.find(p => p.stations.some(s => s.id === topicId));
      if (!path) return null;
      const idx = path.stations.findIndex(s => s.id === topicId);
      const next = path.stations[idx + 1];
      if (!next) return null;
      const d = ALL_DATA[next.id];
      if (!d) return null;
      return { id: next.id, mode: next.mode || "entdecken", label: d.label };
    }

    function AnwendenPanel({ data, topicId, onSelectWithMode }) {
      const list = Array.isArray(data.scenarios) ? data.scenarios : [];

      const [idx, setIdx] = usePersistentState(data.id + ".anwenden.idx", 0);
      const [opened, setOpened] = usePersistentState(data.id + ".anwenden.opened", {});
      const [notes, setNotes] = usePersistentState(data.id + ".anwenden.notes", {});
      const [activeLens, setActiveLens] = useState({});

      if (list.length === 0) return <div style={{ color: textDim, fontSize: "0.8rem" }}>Kein Szenario verfügbar.</div>;

      const safeIdx = Math.min(idx, list.length - 1);
      const s = list[safeIdx];
      const openedHere = opened[safeIdx] || [];
      const current = activeLens[safeIdx] === undefined ? null : activeLens[safeIdx];

      const reflectedSize = Object.keys(opened).filter(k => {
        const ki = Number(k);
        if (ki >= list.length) return false;
        const sc = list[ki];
        const total = (sc.options || []).length;
        return total > 0 && (opened[k] || []).length >= total;
      }).length;

      function toggleLens(oi) {
        const next = current === oi ? null : oi;
        setActiveLens({ ...activeLens, [safeIdx]: next });
        if (!openedHere.includes(oi)) {
          setOpened({ ...opened, [safeIdx]: [...openedHere, oi] });
        }
      }

      function go(delta) {
        const next = safeIdx + delta;
        if (next < 0 || next >= list.length) return;
        setIdx(next);
      }

      React.useEffect(() => {
        function onKey(e) {
          if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
          if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
          if (e.key === "ArrowLeft")  { e.preventDefault(); go(-1); }
          if (["1","2","3","4"].includes(e.key)) toggleLens(parseInt(e.key) - 1);
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
      }, [safeIdx, list.length]);

      React.useEffect(() => {
        let sx = 0, sy = 0;
        function onTouchStart(e) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }
        function onTouchEnd(e) {
          const dx = e.changedTouches[0].clientX - sx;
          const dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
          if (dx < 0) go(1); else go(-1);
        }
        document.addEventListener("touchstart", onTouchStart, { passive: true });
        document.addEventListener("touchend", onTouchEnd, { passive: true });
        return () => { document.removeEventListener("touchstart", onTouchStart); document.removeEventListener("touchend", onTouchEnd); };
      }, [safeIdx, list.length]);

      return (
        <div>
          <div style={{ height: 2, background: border, borderRadius: 2, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ height: "100%", background: accent, width: (list.length ? (reflectedSize / list.length) * 100 : 0) + "%", transition: "width 0.4s", borderRadius: 2 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "13px 16px", background: surface, border: "1px solid " + border, borderRadius: 12, marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: "Georgia, serif", fontSize: "1.7rem", color: accent, lineHeight: 1 }}>{reflectedSize}<span style={{ fontSize: "0.9rem", color: textDim }}> / {list.length}</span></div>
              <div style={{ fontSize: "0.65rem", color: textDim }}>Szenarien reflektiert</div>
            </div>
            <div style={{ width: 1, height: 26, background: border }} />
            <div style={{ fontSize: "0.7rem", color: textMuted, lineHeight: 1.6 }}>Was du unterscheidest –<br />wird spürbar.</div>
          </div>
          {reflectedSize === list.length && list.length > 0 && (
            <div style={{ marginBottom: 14, padding: "10px 14px", background: accentDim, border: "1px solid " + accentBorder, borderRadius: 8, fontSize: "0.7rem", color: accent, textAlign: "center" }}>
              Alle Szenarien reflektiert.
            </div>
          )}
          <div style={{ background: surface, border: "1px solid " + border, borderRadius: 12, padding: "20px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 10 }}>
              <div style={{ display: "inline-flex", alignItems: "center", background: accentDim, border: "1px solid " + accentBorder, color: accent, borderRadius: 100, padding: "3px 11px", fontSize: "0.65rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>◈ Szenario {safeIdx + 1}/{list.length}{s.badge ? " · " + s.badge : ""}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => go(-1)} disabled={safeIdx === 0}
                  style={{ background: surface2, border: "1px solid " + border, color: safeIdx === 0 ? textDim : "#e8ede9", borderRadius: 8, padding: "5px 10px", fontSize: "0.65rem", cursor: safeIdx === 0 ? "default" : "pointer" }}>← Zurück</button>
                <button onClick={() => go(1)} disabled={safeIdx === list.length - 1}
                  style={{ background: surface2, border: "1px solid " + border, color: safeIdx === list.length - 1 ? textDim : "#e8ede9", borderRadius: 8, padding: "5px 10px", fontSize: "0.65rem", cursor: safeIdx === list.length - 1 ? "default" : "pointer" }}>Nächstes →</button>
              </div>
            </div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: "1.08rem", lineHeight: 1.55, color: "#e8ede9", marginBottom: 14 }}>{s.text}</div>
            {s.context && <div style={{ fontSize: "0.7rem", color: textMuted, lineHeight: 1.7, padding: "10px 12px", background: surface2, borderRadius: 8, borderLeft: "2px solid " + borderLight, marginBottom: 16 }}>{s.context}</div>}
            {s.prompt && <div style={{ fontSize: "0.7rem", color: textMuted, marginBottom: 10 }}>{s.prompt}</div>}
            <div style={{ display: "grid", gap: 7 }}>
              {(s.options || []).map((opt, oi) => {
                const isActive = current === oi;
                const hasOpened = openedHere.includes(oi);
                const lens = (s.optionReflections && s.optionReflections[oi] && s.optionReflections[oi].lens) || null;
                return (
                  <button key={oi} onClick={() => toggleLens(oi)}
                    style={{ position: "relative", background: surface2, border: "1px solid " + (isActive ? accent : border), color: "#e8ede9", borderRadius: 8, padding: "10px 14px", textAlign: "left", fontSize: "0.7rem", cursor: "pointer", lineHeight: 1.55, display: "block", width: "100%", fontFamily: "inherit" }}>
                    {hasOpened && <span style={{ position: "absolute", top: 10, right: 10, width: 7, height: 7, borderRadius: "50%", background: accent, pointerEvents: "none" }} />}
                    <div style={{ paddingRight: hasOpened ? 14 : 0 }}>{opt}</div>
                    {isActive && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid " + accentBorder, color: textMuted, fontSize: "0.68rem", lineHeight: 1.65 }}>
                        {lens || "Noch keine Lesart erfasst."}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {s.concepts && s.concepts.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid " + border }}>
                <div style={{ fontSize: "0.65rem", letterSpacing: "0.08em", textTransform: "uppercase", color: textDim, marginBottom: 8 }}>Begriffe, die hier tragen</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {s.concepts.map(c => (
                    <span key={c} style={{ background: accentDim, border: "1px solid " + accentBorder, color: accent, borderRadius: 100, padding: "3px 10px", fontSize: "0.65rem" }}>{c}</span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 14, background: bg, border: "1px dashed " + border, borderRadius: 8, padding: "13px 16px" }}>
              <div style={{ fontSize: "0.72rem", color: "#e8ede9", lineHeight: 1.55, marginBottom: 4, fontFamily: "Georgia, serif", fontStyle: "italic" }}>Welchen Unterschied macht es, wenn du die Perspektiven wechselst?</div>
              <div style={{ fontSize: "0.72rem", color: "#e8ede9", lineHeight: 1.55, marginBottom: 10, fontFamily: "Georgia, serif", fontStyle: "italic" }}>Welche Erkenntnisse nimmst du für dich mit?</div>
              <textarea value={notes[safeIdx] || ""} onChange={e => setNotes({ ...notes, [safeIdx]: e.target.value })}
                rows={3}
                style={{ width: "100%", boxSizing: "border-box", background: surface2, border: "1px solid " + border, borderRadius: 6, padding: "8px 10px", color: "#e8ede9", fontSize: "0.72rem", fontFamily: "inherit", lineHeight: 1.55, resize: "vertical" }} />
              {notes[safeIdx] && (
                <button onClick={() => { const n = { ...notes }; delete n[safeIdx]; setNotes(n); }}
                  style={{ background: "none", border: 0, color: textDim, padding: "6px 0 0", fontSize: "0.65rem", cursor: "pointer", fontFamily: "inherit" }}>
                  Zurücksetzen
                </button>
              )}
            </div>

            {safeIdx < list.length - 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                <button onClick={() => go(1)} style={{ background: accentDim, border: "1px solid " + accentBorder, color: accent, borderRadius: 8, padding: "7px 14px", fontSize: "0.68rem", cursor: "pointer" }}>Nächstes Szenario →</button>
              </div>
            )}
            {safeIdx === list.length - 1 && getNextPathStation(topicId) && (
              <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid " + border }}>
                <div style={{ fontSize: "0.62rem", color: textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Nächste Station im Pfad</div>
                <button onClick={() => onSelectWithMode && onSelectWithMode(getNextPathStation(topicId).id, getNextPathStation(topicId).mode)}
                  style={{ background: accent, border: "none", borderRadius: 8, padding: "8px 16px", color: bg, fontSize: "0.72rem", fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#d4584c"}
                  onMouseLeave={e => e.currentTarget.style.background = accent}>
                  {getNextPathStation(topicId).label}
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    // ─── Landing Page ─────────────────────────────────────────────────────────
    function LandingPage({ onSelectGroup, onAbout, onMein, userName }) {
      const mob = useIsMobile();
      const [isFirstVisit] = React.useState(() => !localStorage.getItem("secondorder.v1.global.visited"));

      function handleAbout() {
        localStorage.setItem("secondorder.v1.global.visited", "1");
        onAbout();
      }

      return (
        <div style={{ width: "100%", minHeight: "100dvh", background: bg, display: "flex", flexDirection: "column", alignItems: "center", position: "relative", overflow: "hidden", padding: "0 20px", boxSizing: "border-box" }}>

          {/* Subtiler Hintergrund-Glow */}
          <div style={{ position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, " + accent + "08, transparent 70%)", top: "20%", left: "50%", transform: "translate(-50%,-50%)", pointerEvents: "none" }} />

          {/* Hero */}
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
          <div style={{ textAlign: "center", maxWidth: 680, position: "relative", zIndex: 1, padding: "24px 0" }}>

            {/* Logo mit Wordmark */}
            <svg width={mob ? 96 : 132} viewBox="10 85 380 280" xmlns="http://www.w3.org/2000/svg" style={{ display: "block", margin: mob ? "0 auto 14px" : "0 auto 24px" }}>
              <circle cx="200" cy="192" r="96" stroke={accent} strokeWidth="2" opacity="0.38" fill="none"/>
              <path d="M200,110 C112,110 112,192 200,192" stroke={accent} strokeWidth="9.5" fill="none" strokeLinecap="round"/>
              <path d="M200,192 C288,192 288,274 200,274" stroke={accent} strokeWidth="9.5" fill="none" strokeLinecap="round"/>
              <circle cx="200" cy="110" r="7" fill={accent}/>
              <circle cx="200" cy="274" r="7" fill={accent}/>
              <circle cx="200" cy="192" r="9" fill={bg}/>
              <circle cx="200" cy="192" r="9" stroke={accent} strokeWidth="2.5" fill="none"/>
              <circle cx="200" cy="192" r="4" fill={accent}/>
              <text x="200" y="350" fontFamily="'Raleway', 'Helvetica Neue', Arial, sans-serif" fontWeight="300" fontSize="25" fill="#e8e6e3" textAnchor="middle" letterSpacing="8">SECOND ORDER</text>
            </svg>

            <h1 style={{ fontFamily: "'Raleway', system-ui, sans-serif", fontSize: "clamp(1.75rem,5vw,3rem)", fontWeight: 600, lineHeight: 1.15, color: "#e8e6e3", margin: mob ? "0 0 12px" : "0 0 20px" }}>
              Einen Unterschied machen.
            </h1>

            <p style={{ fontSize: "clamp(0.875rem,2.5vw,1.0625rem)", fontWeight: 300, color: textMuted, lineHeight: 1.7, margin: mob ? "0 0 8px" : "0 0 12px" }}>
              Wer beobachtet, wie beobachtet wird, macht einen Unterschied, der einen Unterschied macht. Ein Werkzeug für alle, die verstehen wollen, wie Wirklichkeit konstruiert wird.
            </p>
            <p style={{ fontSize: "clamp(0.875rem,2.5vw,1.0625rem)", fontWeight: 300, color: textMuted, lineHeight: 1.7, margin: mob ? "0 0 18px" : "0 0 28px" }}>
              Entdecke, verbinde und wende Konzepte an, die diesen Unterschied sichtbar machen.
            </p>

            {/* Gruppen-Buttons */}
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 12 }}>
              {TOPIC_GROUPS.map(g => (
                <button key={g.id} onClick={() => { localStorage.setItem("secondorder.v1.global.visited", "1"); onSelectGroup(g); }}
                  style={{ background: accent, border: "none", borderRadius: 8, padding: "10px 20px", color: "#262729", fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", width: mob ? 220 : undefined }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#d4584c"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = accent; }}>
                  {g.label}
                </button>
              ))}
            </div>

            {/* Zufällig erkunden */}
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <button onClick={() => { localStorage.setItem("secondorder.v1.global.visited", "1"); onSelectGroup({ ids: [TOPIC_ORDER[Math.floor(Math.random() * TOPIC_ORDER.length)]] }); }}
                style={{ background: "transparent", border: "1px solid " + accentBorder, borderRadius: 100, padding: "8px 18px", color: "#e8e6e3", fontSize: "0.75rem", fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}
                onMouseEnter={e => { e.currentTarget.style.background = accentDim; e.currentTarget.style.borderColor = accent; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = accentBorder; }}>
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M2 4h2l1 2M14 4h-2l-4 8H6M14 4l-2 2M14 4l-2-2" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12h2l4-8" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 10l2 2-2 2" stroke="#e8e6e3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Zufällig erkunden
              </button>
            </div>

            {/* Sekundäre Links */}
            <div style={{ marginTop: 20, display: "flex", justifyContent: "center", alignItems: "center", gap: 18 }}>
              <button onClick={handleAbout}
                style={{ background: "transparent", border: "none", fontFamily: "inherit", fontSize: "0.75rem", color: isFirstVisit ? accent : textMuted, cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6 }}
                onMouseEnter={e => { e.currentTarget.style.color = "#e8ede9"; }}
                onMouseLeave={e => { e.currentTarget.style.color = isFirstVisit ? accent : textMuted; }}>
                {isFirstVisit && <span style={{ width: 5, height: 5, borderRadius: "50%", background: accent, flexShrink: 0, display: "inline-block" }} />}
                Mehr dazu
              </button>
              <button onClick={onMein}
                style={{ background: "transparent", border: "none", fontFamily: "inherit", fontSize: "0.75rem", color: textMuted, cursor: "pointer", padding: 0 }}
                onMouseEnter={e => { e.currentTarget.style.color = "#e8ede9"; }}
                onMouseLeave={e => { e.currentTarget.style.color = textMuted; }}>
                Mein Space
              </button>
            </div>

          </div>
          </div>

          {/* Footer-Tagline */}
          <div style={{ fontSize: mob ? "0.52rem" : "0.625rem", color: textMuted, letterSpacing: mob ? "0.08em" : "0.15em", textAlign: "center", padding: "0 0 20px", whiteSpace: "nowrap", position: "relative", zIndex: 1 }}>
            Second Order — Konzepte, die einen Unterschied machen.
          </div>
        </div>
      );
    }

    // ─── About View ───────────────────────────────────────────────────────────
    function AboutView({ onBack, onSelect, onMein, onSelectWithMode }) {
      const labelStyle = { fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", color: accent, marginBottom: 10, fontWeight: 600 };
      const pStyle = { fontSize: "0.88rem", color: textMuted, lineHeight: 1.8, margin: 0 };
      const dividerStyle = { borderTop: "1px solid " + border, margin: "32px 0" };

      const [hoveredPill, setHoveredPill] = React.useState(null);

      const groupDescs = {
        systeme:    "Was hält Systeme zusammen — und warum scheitert Steuerung so oft, obwohl alle das Beste wollen?",
        erkenntnis: "Woher wissen wir, was wir wissen — und was ändert sich, wenn wir als Beobachtende Teil des Beobachteten sind?",
        sprache:    "Sprache beschreibt nicht die Welt — sie erschafft sie. Wer das einmal gesehen hat, hört nie wieder einfach nur zu.",
        psyche:     "Was bewegt Menschen — und warum wiederholen sich Muster, auch wenn niemand sie will?",
        wirkung:    "Was unterscheidet wirksame Beratung von gut gemeinter Intervention?",
      };
      const groupOrder = {
        systeme:    "Wer ein System orientieren will, muss erst verstehen, wie es sich selbst erhält.",
        erkenntnis: "Die radikale Konsequenz: Wir konstruieren, was wir beobachten.",
        sprache:    "Wer spricht, handelt. Wer schweigt, auch.",
        psyche:     "Was sich wiederholt, hat immer auch eine Funktion.",
        wirkung:    "Theorie hilft. Bis sie im Weg steht.",
      };

      return (
        <div style={{ background: bg, minHeight: "calc(100vh - 52px)" }}>

          {/* Zurück-Button */}
          <div style={{ padding: "14px 18px", borderBottom: "1px solid " + border }}>
            <button onClick={onBack}
              style={{ background: "none", border: "none", color: textMuted, fontSize: "0.72rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              ← Zurück
            </button>
          </div>

          <div style={{ padding: "32px 18px 60px", maxWidth: 680, margin: "0 auto" }}>

            {/* Logo */}
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <svg width="120" viewBox="10 85 380 280" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block" }}>
                <circle cx="200" cy="192" r="96" stroke={accent} strokeWidth="2" opacity="0.38" fill="none"/>
                <path d="M200,110 C112,110 112,192 200,192" stroke={accent} strokeWidth="9.5" fill="none" strokeLinecap="round"/>
                <path d="M200,192 C288,192 288,274 200,274" stroke={accent} strokeWidth="9.5" fill="none" strokeLinecap="round"/>
                <circle cx="200" cy="110" r="7" fill={accent}/>
                <circle cx="200" cy="274" r="7" fill={accent}/>
                <circle cx="200" cy="192" r="9" fill={bg}/>
                <circle cx="200" cy="192" r="9" stroke={accent} strokeWidth="2.5" fill="none"/>
                <circle cx="200" cy="192" r="4" fill={accent}/>
                <text x="200" y="350" fontFamily="'Raleway', 'Helvetica Neue', Arial, sans-serif" fontWeight="300" fontSize="25" fill="#e8e6e3" textAnchor="middle" letterSpacing="8">SECOND ORDER</text>
              </svg>
            </div>

            {/* Was ist Second Order? */}
            <div style={{ marginBottom: 32 }}>
              <div style={labelStyle}>Was ist Second Order?</div>
              <p style={pStyle}>
                Die meisten Werkzeuge helfen, Probleme zu lösen. Second Order hilft dir, einen Schritt zurück zu treten und deine eigene Wirklichkeit als Konstruktion zu erkennen.
              </p>
              <p style={{ ...pStyle, marginTop: 12 }}>
                Der Name stammt von Heinz von Foerster: Kybernetik zweiter Ordnung — nicht mehr das System beobachten, sondern beobachten, wie beobachtet wird. Gregory Bateson nannte das einen Unterschied, der einen Unterschied macht.
              </p>
              <p style={{ ...pStyle, marginTop: 12 }}>
                Bedeutende Konzepte aus Systemtheorie, Konstruktivismus und Kybernetik. Eine Einladung, das eigene Denken zu überdenken.
              </p>
            </div>

            <div style={dividerStyle} />

            {/* Wie funktioniert Second Order? */}
            <div style={{ marginBottom: 32 }}>
              <div style={labelStyle}>Wie funktioniert Second Order?</div>
              {[
                { id: "entdecken", num: "01", title: "Entdecken", text: "Zehn Begriffe pro Denker*in — jeder aufbereitet mit Beschreibung, Analogie, konkretem Beispiel, Erkennungsmerkmalen und Quellen. Entdecken legt das Fundament: Wer einen Begriff wirklich kennt, erkennt ihn später in der Welt." },
                { id: "vernetzen", num: "02", title: "Verbinden",  text: "Verbinden stellt Situationen vor, die keine eindeutige Antwort haben — und zeigt, wie verschiedene Konzepte derselben Denker*in dieselbe Situation unterschiedlich beleuchten. Das schärft die Unterscheidungsfähigkeit. Zusätzlich zeigen Begriffsbeziehungen die Verbindungen der einzelnen Konzepte auf." },
                { id: "anwenden",  num: "03", title: "Anwenden",  text: "Drei praxisnahe Szenarien, vier Lesarten. Jede zeigt, was sichtbar wird, wenn man ein Konzept als Linse nimmt — ohne zu fragen, was richtig ist. Anwenden macht die Beobachtung zweiter Ordnung zur Praxis. Zusätzlich gibt es pro Szenario Reflexionsfragen mit der Möglichkeit, eigene Erkenntnisse als Notizen festzuhalten." },
              ].map(({ id, num, title, text }) => (
                <div key={id} style={{ marginBottom: 20 }}>
                  <button onClick={() => onSelectWithMode && onSelectWithMode("watzlawick", id)}
                    style={{ background: "none", border: "none", padding: "0 0 6px", cursor: "pointer", display: "flex", alignItems: "baseline", gap: 8, fontFamily: "inherit" }}>
                    <span style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: accent }}>{num}</span>
                    <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#e8e6e3", letterSpacing: "0.02em" }}>{title}</span>
                  </button>
                  <p style={{ ...pStyle, fontSize: "0.82rem" }}>{text}</p>
                </div>
              ))}
            </div>

            <div style={dividerStyle} />

            {/* Die 5 Felder */}
            <div>
              <div style={{ ...labelStyle, marginBottom: 20 }}>Die 5 Kategorien</div>
              {TOPIC_GROUPS.map((g, i) => (
                <div key={g.id} style={{ marginBottom: i < TOPIC_GROUPS.length - 1 ? 28 : 0 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#e8e6e3", marginBottom: 4 }}>{g.label}</div>
                  <p style={{ ...pStyle, fontSize: "0.82rem", marginBottom: 4 }}>{groupDescs[g.id]}</p>
                  <p style={{ ...pStyle, fontSize: "0.72rem", color: textDim, fontStyle: "italic", marginBottom: 8 }}>{groupOrder[g.id]}</p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {g.ids.map(id => {
                      const t = TOPICS.find(t => t.id === id);
                      const headline = THINKER_HEADLINES[id];
                      return (
                        <div key={id} style={{ position: "relative" }}>
                          <button onClick={() => onSelect(id)}
                            style={{ background: surface, border: "1px solid " + border, borderRadius: 100, padding: "4px 12px", color: textMuted, fontSize: "0.68rem", cursor: "pointer" }}
                            onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.borderColor = accentBorder; setHoveredPill(id); }}
                            onMouseLeave={e => { e.currentTarget.style.color = textMuted; e.currentTarget.style.borderColor = border; setHoveredPill(null); }}>
                            {t.label}
                          </button>
                          {hoveredPill === id && headline && (
                            <div style={{ position: "absolute", bottom: "calc(100% + 7px)", left: "50%", transform: "translateX(-50%)", background: surface2, border: "1px solid " + borderLight, borderRadius: 6, padding: "5px 10px", fontSize: "0.68rem", color: "#e8e6e3", whiteSpace: "nowrap", zIndex: 20, pointerEvents: "none", fontFamily: "'Raleway', system-ui, sans-serif" }}>
                              {headline}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={dividerStyle} />

            {/* Mein Space */}
            <div style={{ marginBottom: 32 }}>
              <div style={labelStyle}>Mein Space</div>
              {[
                { id: "erkundung",  num: "01", title: "Erkundungsreise", text: "Alle Konzepte auf einen Blick, geordnet nach den fünf Kategorien. Die Erkundungsreise zeigt, was du bereits kennst, was du vertieft hast — und macht neugierig auf das, was noch auf dich wartet." },
                { id: "lernpfade", num: "02", title: "Lernpfade",        text: "Lernpfade verbinden Konzepte zu einer Reise entlang von Fragen, die quer durch die Kategorien führen. Entdecke die angelegten Pfade oder stelle eigene zusammen." },
                { id: "landkarte", num: "03", title: "Landkarte",        text: "Die Landkarte macht sichtbar, was im Text verborgen bleibt: wer wen beeinflusst hat, welche Ideen Konzepte aus verschiedenen Welten verbinden." },
              ].map(({ id, num, title, text }) => (
                <div key={id} style={{ marginBottom: 20 }}>
                  <button onClick={() => onMein && onMein(id)}
                    style={{ background: "none", border: "none", padding: "0 0 6px", cursor: "pointer", display: "flex", alignItems: "baseline", gap: 8, fontFamily: "inherit" }}>
                    <span style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: accent }}>{num}</span>
                    <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "#e8e6e3", letterSpacing: "0.02em" }}>{title}</span>
                  </button>
                  <p style={{ ...pStyle, fontSize: "0.82rem" }}>{text}</p>
                </div>
              ))}
            </div>

            <div style={dividerStyle} />

            {/* Footer */}
            <div style={{ borderTop: "1px solid " + border + "22", paddingTop: 28, textAlign: "center" }}>
              <p style={{ fontSize: "0.7rem", color: textDim, lineHeight: 1.7, marginBottom: 20, maxWidth: 560, margin: "0 auto 20px" }}>
                Die Inhalte dieser Seite sind KI-generiert und redaktionell überarbeitet. Sie dienen ausschließlich Bildungszwecken und sind nicht von den genannten Denker*innen autorisiert. Zitate können Paraphrasen sein. Für die Richtigkeit der Quellenangaben wird keine Gewähr übernommen.
              </p>
              <div style={{ fontSize: "0.8rem", color: textMuted, marginBottom: 12 }}>
                Crafted with <span style={{ color: accent }}>♥</span> by <a href="https://www.marcelbinzenhoefer.de/" target="_blank" rel="noopener noreferrer" style={{ color: "#e8e6e3", fontWeight: 600, textDecoration: "none" }}>Marcel Binzenhöfer</a> & Claude Code
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 32 }}>
                <a href="https://www.marcelbinzenhoefer.de/impressum" target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.75rem", color: textMuted, textDecoration: "none", fontWeight: 600 }}>Imprint</a>
                <a href="https://www.marcelbinzenhoefer.de/datenschutz" target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.75rem", color: textMuted, textDecoration: "none", fontWeight: 600 }}>Privacy</a>
              </div>
            </div>

          </div>
        </div>
      );
    }

    // ─── Group Nav ────────────────────────────────────────────────────────────
    function GroupNav({ topicId, onSelect }) {
      const mob = useIsMobile();
      const activeGroup = TOPIC_GROUPS.find(g => g.ids.includes(topicId));
      const [selectedGroupId, setSelectedGroupId] = React.useState(activeGroup?.id || TOPIC_GROUPS[0].id);

      React.useEffect(() => {
        const g = TOPIC_GROUPS.find(g => g.ids.includes(topicId));
        if (g) setSelectedGroupId(g.id);
      }, [topicId]);

      const selectedGroup = TOPIC_GROUPS.find(g => g.id === selectedGroupId);

      return (
        <div style={{ borderBottom: "1px solid " + border }}>
          {/* Gruppen-Tabs */}
          <div style={{ display: "flex", flexWrap: mob ? "nowrap" : "nowrap", overflowX: mob ? "visible" : "auto", padding: mob ? "0 10px" : "0 10px" }}>
            {TOPIC_GROUPS.map(g => {
              const isSelected = selectedGroupId === g.id;
              const label = mob ? g.label.split(" & ")[0] : g.label;
              return (
                <button key={g.id} onClick={() => setSelectedGroupId(g.id)}
                  style={{ background: "none", border: "none", padding: mob ? "9px 4px" : "9px 0", color: isSelected ? accent : textMuted, fontSize: mob ? "0.6rem" : "0.67rem", cursor: "pointer", letterSpacing: "0.02em", whiteSpace: "nowrap", flex: "1 1 0", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", textAlign: "center" }}>
                  {label}
                </button>
              );
            })}
          </div>
          {/* Denker-Pills */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, padding: "8px 14px 10px" }}>
            {selectedGroup.ids.map(id => {
              const t = TOPICS.find(t => t.id === id);
              const isCurrent = topicId === id;
              return (
                <button key={id} onClick={() => onSelect(id)}
                  style={{ background: isCurrent ? accentDim : surface, border: "1px solid " + (isCurrent ? accentBorder : border), borderRadius: 100, padding: "5px 12px", color: isCurrent ? accent : textMuted, fontSize: "0.68rem", cursor: "pointer" }}
                  onMouseEnter={e => { if (!isCurrent) { e.currentTarget.style.color = accent; e.currentTarget.style.borderColor = accentBorder; } }}
                  onMouseLeave={e => { if (!isCurrent) { e.currentTarget.style.color = textMuted; e.currentTarget.style.borderColor = border; } }}>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // ─── SearchOverlay ────────────────────────────────────────────────────────
    function SearchOverlay({ index, onSelectThinker, onSelectTerm, onClose }) {
      const [query, setQuery] = React.useState("");
      const [focusIdx, setFocusIdx] = React.useState(0);
      const inputRef = React.useRef(null);

      React.useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
      }, []);

      const results = React.useMemo(() => search(query, index), [query, index]);
      const flat = [...results.thinkers.map(t => ({ type: "thinker", ...t })),
                    ...results.terms.map(t => ({ type: "term", ...t }))];

      React.useEffect(() => { setFocusIdx(0); }, [query]);

      function handleKey(e) {
        if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx(i => Math.min(i + 1, flat.length - 1)); }
        if (e.key === "ArrowUp")   { e.preventDefault(); setFocusIdx(i => Math.max(i - 1, 0)); }
        if (e.key === "Enter" && flat[focusIdx]) { select(flat[focusIdx]); }
        if (e.key === "Escape") onClose();
      }

      function select(item) {
        if (item.type === "thinker") onSelectThinker(item.id);
        else onSelectTerm(item.id, item.term);
        onClose();
      }

      const hasResults = flat.length > 0;

      return (
        <div
          onClick={onClose}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 80 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key !== "Tab") return;
              const el = e.currentTarget;
              const focusable = Array.from(el.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])')).filter(n => !n.disabled);
              if (!focusable.length) return;
              const first = focusable[0], last = focusable[focusable.length - 1];
              if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
              else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }}
            style={{ background: surface, border: "1px solid " + border, borderRadius: 10, width: "min(560px, calc(100vw - 32px))", maxHeight: "min(480px, 70vh)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}
          >
            {/* Suchfeld */}
            <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid " + border, gap: 10 }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="6.5" cy="6.5" r="4.5" stroke={textDim} strokeWidth="1.5"/>
                <line x1="10" y1="10" x2="14" y2="14" stroke={textDim} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Denker*in oder Begriff suchen…"
                style={{ flex: 1, background: "none", border: "none", outline: "none", color: "#e8e6e3", fontSize: "0.9rem", fontFamily: "'Raleway', system-ui, sans-serif" }}
              />
              {query && (
                <button onClick={() => setQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: textDim, fontSize: "0.8rem", padding: 0, lineHeight: 1 }}>✕</button>
              )}
            </div>

            {/* Ergebnisse */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {!query && (
                <div style={{ padding: "20px 16px", color: textDim, fontSize: "0.75rem", textAlign: "center" }}>
                  Tippe eine*n Denker*in oder Begriff
                </div>
              )}
              {query && !hasResults && (
                <div style={{ padding: "20px 16px", color: textDim, fontSize: "0.75rem", textAlign: "center" }}>
                  Keine Treffer für „{query}"
                </div>
              )}
              {results.thinkers.length > 0 && (
                <>
                  <div style={{ padding: "8px 16px 4px", fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: textDim }}>Denker*innen</div>
                  {results.thinkers.map((t, i) => {
                    const idx = i;
                    const focused = focusIdx === idx;
                    return (
                      <button
                        key={t.id}
                        onClick={() => select({ type: "thinker", ...t })}
                        onMouseEnter={() => setFocusIdx(idx)}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", background: focused ? accentDim : "none", border: "none", cursor: "pointer", textAlign: "left", color: focused ? "#e8e6e3" : textMuted, fontSize: "0.82rem" }}
                      >
                        <span style={{ color: accent, fontSize: "0.65rem", width: 14, flexShrink: 0 }}>◆</span>
                        <span>{t.title} <span style={{ color: textDim, fontStyle: "italic" }}>{t.label}</span></span>
                      </button>
                    );
                  })}
                </>
              )}
              {results.terms.length > 0 && (
                <>
                  <div style={{ padding: "8px 16px 4px", fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: textDim, borderTop: results.thinkers.length > 0 ? "1px solid " + border : "none", marginTop: results.thinkers.length > 0 ? 4 : 0 }}>Begriffe</div>
                  {results.terms.map((t, i) => {
                    const idx = results.thinkers.length + i;
                    const focused = focusIdx === idx;
                    return (
                      <button
                        key={t.id + "-" + t.term}
                        onClick={() => select({ type: "term", ...t })}
                        onMouseEnter={() => setFocusIdx(idx)}
                        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "9px 16px", background: focused ? accentDim : "none", border: "none", cursor: "pointer", textAlign: "left", color: focused ? "#e8e6e3" : textMuted, fontSize: "0.82rem" }}
                      >
                        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ color: borderLight, fontSize: "0.65rem", width: 14, flexShrink: 0 }}>◇</span>
                          <span>{t.term}</span>
                        </span>
                        <span style={{ color: textDim, fontSize: "0.7rem", whiteSpace: "nowrap", flexShrink: 0 }}>→ {t.thinkerLabel}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    // ─── ErkundungsPanel ──────────────────────────────────────────────────────
    function getThinkerProgress(id) {
      const prefix = STORAGE_PREFIX + id + ".";
      function load(key) {
        try { return JSON.parse(localStorage.getItem(prefix + key)); } catch { return null; }
      }
      const seen     = load("entdecken.seen")     || [];
      const answers  = load("vernetzen.answers")  || {};
      const explored = load("vernetzen.explored") || [];
      const opened   = load("anwenden.opened")    || {};
      if (seen.length === 0) return "unberuehrt";
      const vernetzt  = Object.keys(answers).length > 0 || explored.length > 0;
      const angewandt = Object.values(opened).some(a => Array.isArray(a) && a.length > 0);
      return (vernetzt && angewandt) ? "vertieft" : "angefangen";
    }

    function ErkundungsPanel({ onSelect }) {
      const [openGroup, setOpenGroup] = React.useState(null);
      const [resetTick, setResetTick] = React.useState(0);
      const lastVisited = localStorage.getItem(STORAGE_PREFIX + "global.lastVisited");
      const lastData = lastVisited ? ALL_DATA[lastVisited] : null;

      function resetThinker(id, e) {
        e.stopPropagation();
        Object.keys(localStorage)
          .filter(k => k.startsWith(STORAGE_PREFIX + id + "."))
          .forEach(k => localStorage.removeItem(k));
        setResetTick(t => t + 1);
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

          {lastData && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: "0.6rem", letterSpacing: "0.12em", textTransform: "uppercase", color: textDim, marginBottom: 8 }}>Zuletzt erkundet</div>
              <button onClick={() => onSelect(lastVisited)}
                style={{ background: accentDim, border: "1px solid " + accentBorder, borderRadius: 20, padding: "5px 14px", color: accent, fontSize: "0.72rem", fontFamily: "inherit", cursor: "pointer", fontWeight: 600 }}
                onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.color = bg; }}
                onMouseLeave={e => { e.currentTarget.style.background = accentDim; e.currentTarget.style.color = accent; }}>
                {lastData.label}
              </button>
            </div>
          )}
          {TOPIC_GROUPS.map(group => {
            const progressList = group.ids.map(id => getThinkerProgress(id));
            const started = progressList.filter(p => p !== "unberuehrt").length;
            const pct = (started / group.ids.length) * 100;
            const isOpen = openGroup === group.id;

            return (
              <div key={group.id} style={{ background: surface, borderRadius: 10, border: "1px solid " + border, overflow: "hidden" }}>

                {/* Gruppen-Header */}
                <button
                  onClick={() => setOpenGroup(isOpen ? null : group.id)}
                  aria-expanded={isOpen}
                  style={{ width: "100%", background: "none", border: "none", padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, textAlign: "left", transition: "background 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#e8e6e3", letterSpacing: "0.01em", marginBottom: 8 }}>
                      {group.label}
                    </div>
                    {/* Fortschrittsbalken */}
                    <div style={{ height: 3, borderRadius: 2, background: border, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: pct === 100 ? accent : accentDim, borderRadius: 2, transition: "width 0.3s ease" }} />
                    </div>
                  </div>
                  <span style={{ fontSize: "0.65rem", color: textDim, flexShrink: 0, minWidth: 36, textAlign: "right" }}>
                    {started}/10
                  </span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                    <path d="M2 4l4 4 4-4" stroke={textDim} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {isOpen && (
                  <div style={{ padding: "0 16px 14px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {group.ids.map(id => {
                      const p = getThinkerProgress(id);
                      const d = ALL_DATA[id];
                      const label = d ? d.label : id;
                      const hasProgress = p !== "unberuehrt";
                      const styles = p === "vertieft"
                        ? { background: accent, color: bg, border: "1px solid " + accent }
                        : p === "angefangen"
                        ? { background: accentDim, color: accent, border: "1px solid " + accentBorder }
                        : { background: "transparent", color: textDim, border: "1px solid " + borderLight };
                      return (
                        <div key={id} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                          <button
                            onClick={() => onSelect(id)}
                            title={p === "vertieft" ? "Vertieft" : p === "angefangen" ? "Angefangen" : "Noch nicht erkundet"}
                            style={{ ...styles, borderRadius: 20, padding: "4px 10px", fontSize: "0.65rem", fontFamily: "inherit", cursor: "pointer", fontWeight: p === "vertieft" ? 600 : 400, letterSpacing: "0.01em" }}
                          >
                            {label}
                          </button>
                          {hasProgress && (
                            <button
                              onClick={e => resetThinker(id, e)}
                              aria-label={"Fortschritt von " + label + " zuruecksetzen"}
                              title="Fortschritt zurücksetzen"
                              style={{ background: "none", border: "none", cursor: "pointer", color: textDim, padding: "2px 3px", fontSize: "0.65rem", lineHeight: 1, borderRadius: 4 }}
                              onMouseEnter={e => { e.currentTarget.style.color = danger; }}
                              onMouseLeave={e => { e.currentTarget.style.color = textDim; }}
                            >↺</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // ─── INTELLECTUAL_LINKS ───────────────────────────────────────────────────
    INTELLECTUAL_LINKS.forEach(link => { link.desc = germanize(link.desc); });

    // ─── computeForcePositions ────────────────────────────────────────────────
    function computeForcePositions(nodes, edges, W, H) {
      const N = nodes.length;
      const k = Math.sqrt((W * H) / N) * 0.9;
      const nodeIdx = {};
      nodes.forEach((n, i) => { nodeIdx[n.id] = i; });

      const pos = nodes.map(({ id, gi, ti }) => {
        const ga = (gi / 5) * 2 * Math.PI - Math.PI / 2;
        const gr = Math.min(W, H) * 0.28;
        const cx = W / 2 + Math.cos(ga) * gr;
        const cy = H / 2 + Math.sin(ga) * gr;
        const la = (ti / 10) * 2 * Math.PI;
        return { id, x: cx + Math.cos(la) * 30, y: cy + Math.sin(la) * 30, fx: 0, fy: 0 };
      });

      for (let iter = 0; iter < 300; iter++) {
        const temp = Math.max(0.5, (1 - iter / 300) * 20);
        pos.forEach(p => { p.fx = 0; p.fy = 0; });

        for (let i = 0; i < N; i++) {
          for (let j = i + 1; j < N; j++) {
            const dx = pos[i].x - pos[j].x;
            const dy = pos[i].y - pos[j].y;
            const d2 = dx * dx + dy * dy || 0.01;
            const d = Math.sqrt(d2);
            const f = k * k / d2;
            pos[i].fx += f * dx; pos[i].fy += f * dy;
            pos[j].fx -= f * dx; pos[j].fy -= f * dy;
          }
        }

        edges.forEach(([a, b]) => {
          const i = nodeIdx[a], j = nodeIdx[b];
          if (i === undefined || j === undefined) return;
          const dx = pos[j].x - pos[i].x;
          const dy = pos[j].y - pos[i].y;
          const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
          const f = d * d / k;
          pos[i].fx += f * dx / d; pos[i].fy += f * dy / d;
          pos[j].fx -= f * dx / d; pos[j].fy -= f * dy / d;
        });

        pos.forEach(p => {
          p.fx += (W / 2 - p.x) * 0.04;
          p.fy += (H / 2 - p.y) * 0.04;
        });

        pos.forEach(p => {
          const flen = Math.sqrt(p.fx * p.fx + p.fy * p.fy);
          if (flen > 0) {
            const disp = Math.min(flen, temp);
            p.x += (p.fx / flen) * disp;
            p.y += (p.fy / flen) * disp;
          }
          p.x = Math.max(18, Math.min(W - 18, p.x));
          p.y = Math.max(18, Math.min(H - 18, p.y));
        });
      }

      const result = {};
      pos.forEach(p => { result[p.id] = { x: p.x, y: p.y }; });
      return result;
    }

    // ─── GraphPanel ───────────────────────────────────────────────────────────
    function GraphPanel({ onSelect }) {
      const mob = useIsMobile();
      const containerRef = React.useRef(null);
      const [graphW, setGraphW] = React.useState(mob ? 320 : 500);
      React.useEffect(() => {
        if (!containerRef.current) return;
        const update = () => {
          if (!containerRef.current) return;
          const total = containerRef.current.offsetWidth;
          setGraphW(mob ? total : Math.min(Math.floor(total * 0.52), 800));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(containerRef.current);
        return () => ro.disconnect();
      }, [mob]);
      const W = graphW;
      const H = Math.round(W * 0.8);
      const DR = 9;
      const GROUP_COLORS = ["#6b9fcf", "#7fc98a", "#cf9ea0", "#cfb87f", "#f57669"];
      const GROUP_SHORT   = ["Systeme", "Erkenntnis", "Sprache", "Psyche", "Wirkung"];

      const allNodes = React.useMemo(() =>
        TOPIC_GROUPS.flatMap((group, gi) => group.ids.map((id, ti) => ({ id, gi, ti }))),
        []
      );

      const basePositions = React.useMemo(() =>
        computeForcePositions(allNodes, INTELLECTUAL_LINKS.map(l => [l.a, l.b]), W, H),
        [W]
      );

      const [nodePos, setNodePos] = React.useState(basePositions);
      const [hovered, setHovered] = React.useState(null);
      const [focused, setFocused] = React.useState(null);
      const [drag, setDrag] = React.useState(null);
      const dragMoved = React.useRef(false);
      const svgRef = React.useRef(null);

      React.useEffect(() => { setNodePos(basePositions); }, [basePositions]);

      const neighborSet = React.useMemo(() => {
        if (!focused) return null;
        const s = new Set([focused]);
        INTELLECTUAL_LINKS.forEach(({ a, b }) => {
          if (a === focused) s.add(b);
          if (b === focused) s.add(a);
        });
        return s;
      }, [focused]);

      function svgCoords(e) {
        const rect = svgRef.current.getBoundingClientRect();
        return {
          x: (e.clientX - rect.left) * (W / rect.width),
          y: (e.clientY - rect.top)  * (H / rect.height)
        };
      }

      function onCircleMouseDown(e, id) {
        e.preventDefault();
        dragMoved.current = false;
        const { x, y } = svgCoords(e);
        const p = nodePos[id];
        setDrag({ id, ox: x - p.x, oy: y - p.y });
      }

      function onSvgMouseMove(e) {
        if (!drag) return;
        dragMoved.current = true;
        const { x, y } = svgCoords(e);
        setNodePos(prev => ({
          ...prev,
          [drag.id]: {
            x: Math.max(DR, Math.min(W - DR, x - drag.ox)),
            y: Math.max(DR, Math.min(H - DR, y - drag.oy))
          }
        }));
      }

      function onNodeClick(id) {
        if (dragMoved.current) return;
        setFocused(prev => prev === id ? null : id);
      }

      const connections = focused ? INTELLECTUAL_LINKS.filter(l => l.a === focused || l.b === focused) : [];

      return (
        <div ref={containerRef} style={{ display: "flex", flexDirection: mob ? "column" : "row", gap: 24, alignItems: "flex-start" }}>

          {/* Graph */}
          <div style={{ flexShrink: 0, width: mob ? "100%" : W }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              style={{ width: "100%", display: "block", cursor: drag ? "grabbing" : "default" }}
              onMouseMove={onSvgMouseMove}
              onMouseUp={() => setDrag(null)}
              onMouseLeave={() => setDrag(null)}
              onClick={e => { if (e.target === svgRef.current) setFocused(null); }}
            >
              {/* Kanten */}
              {INTELLECTUAL_LINKS.map(({ a, b }) => {
                const pa = nodePos[a], pb = nodePos[b];
                if (!pa || !pb) return null;
                const dim = neighborSet && !(neighborSet.has(a) && neighborSet.has(b));
                return <line key={a + b} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
                  stroke={border} strokeWidth={dim ? 0.5 : 1.5} opacity={dim ? 0.08 : 0.55} />;
              })}

              {/* Knoten */}
              {allNodes.map(({ id, gi }) => {
                const p = nodePos[id];
                if (!p) return null;
                const prog = getThinkerProgress(id);
                const baseOpacity = prog === "vertieft" ? 1 : prog === "angefangen" ? 0.72 : 0.28;
                const dimmed = neighborSet && !neighborSet.has(id);
                const opacity = dimmed ? 0.07 : baseOpacity;
                const color = GROUP_COLORS[gi];
                const isFocused = id === focused;
                const isNeighbor = neighborSet && neighborSet.has(id) && id !== focused;
                return (
                  <g key={id}
                    style={{ cursor: drag && drag.id === id ? "grabbing" : "grab" }}
                    onMouseEnter={() => setHovered(id)}
                    onMouseLeave={() => setHovered(null)}
                    onMouseDown={e => onCircleMouseDown(e, id)}
                    onClick={() => onNodeClick(id)}
                  >
                    {prog === "vertieft" && !dimmed && (
                      <circle cx={p.x} cy={p.y} r={DR + 3}
                        fill="none" stroke={accent} strokeWidth={1.5} opacity={0.55} />
                    )}
                    {isFocused && (
                      <circle cx={p.x} cy={p.y} r={DR + 4}
                        fill="none" stroke="#e8e6e3" strokeWidth={2} opacity={0.9} />
                    )}
                    {isNeighbor && (
                      <circle cx={p.x} cy={p.y} r={DR + 3}
                        fill="none" stroke={color} strokeWidth={1.5} opacity={0.7} />
                    )}
                    <circle cx={p.x} cy={p.y} r={DR} fill={color} opacity={opacity} />
                  </g>
                );
              })}

              {/* Tooltip (nur wenn kein focused) */}
              {!focused && hovered && nodePos[hovered] && (() => {
                const p = nodePos[hovered];
                const d = ALL_DATA[hovered];
                const label = d ? d.label : hovered;
                const prog = getThinkerProgress(hovered);
                const progLabel = prog === "vertieft" ? "Vertieft" : prog === "angefangen" ? "Angefangen" : "Noch nicht erkundet";
                const tw = Math.max(label.length, progLabel.length) * 6.5 + 18;
                const tx = Math.min(Math.max(p.x, tw / 2 + 4), W - tw / 2 - 4);
                const ty = Math.max(p.y - DR - 30, 18);
                return (
                  <g style={{ pointerEvents: "none" }}>
                    <rect x={tx - tw / 2} y={ty - 14} width={tw} height={32}
                      rx={5} fill={surface2} stroke={border} strokeWidth={0.8} />
                    <text x={tx} y={ty} textAnchor="middle" fontSize="11"
                      fill="#e8e6e3" fontFamily="'Raleway', system-ui, sans-serif" fontWeight="600">
                      {label}
                    </text>
                    <text x={tx} y={ty + 13} textAnchor="middle" fontSize="9"
                      fill={textDim} fontFamily="'Raleway', system-ui, sans-serif">
                      {progLabel}
                    </text>
                  </g>
                );
              })()}
            </svg>

            {/* Legende */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginTop: 10, padding: "0 4px" }}>
              {TOPIC_GROUPS.map((group, gi) => (
                <div key={group.id} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <svg width="10" height="10" viewBox="0 0 10 10">
                    <circle cx="5" cy="5" r="4" fill={GROUP_COLORS[gi]} />
                  </svg>
                  <span style={{ fontSize: "0.62rem", color: textDim }}>{GROUP_SHORT[gi]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Info-Panel */}
          <div style={{ flex: 1, minWidth: mob ? "100%" : 300 }}>
            {!focused ? (
              <div style={{ padding: "12px 0" }}>
                <p style={{ fontSize: "0.75rem", color: textDim, lineHeight: 1.6 }}>
                  Klick auf einen Knoten, um seine intellektuellen Verbindungen zu erkunden.
                </p>
              </div>
            ) : (() => {
              const d = ALL_DATA[focused];
              return (
                <div>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: accent, marginBottom: 4 }}>
                      {d ? d.subtitle : ""}
                    </div>
                    <h2 style={{ fontFamily: "Georgia, serif", fontSize: "1.1rem", fontWeight: 400, color: "#e8e6e3", margin: 0, lineHeight: 1.2 }}>
                      {d ? d.title : focused} {d ? <em style={{ color: "#f5a99e" }}>{d.titleEm}</em> : null}
                    </h2>
                    <div style={{ fontSize: "0.65rem", color: textDim, marginTop: 6 }}>
                      {connections.length} Verbindung{connections.length !== 1 ? "en" : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {connections.map(({ a, b, desc }) => {
                      const otherId = a === focused ? b : a;
                      const od = ALL_DATA[otherId];
                      const mapB = new Map((ALL_DATA[otherId]?.cards || []).map(c => [c.term, c]));
                      const shared = (ALL_DATA[focused]?.cards || []).filter(c => mapB.has(c.term)).map(c => ({ term: c.term, defA: c.definition, defB: mapB.get(c.term).definition }));
                      return (
                        <div key={otherId} style={{ borderLeft: "2px solid " + border, paddingLeft: 10 }}>
                          <div style={{ fontSize: "0.7rem", fontWeight: 600, color: "#e8e6e3", marginBottom: 4 }}>
                            ↔ {od ? od.label : otherId}
                          </div>
                          <p style={{ fontSize: "0.72rem", color: textMuted, lineHeight: 1.65, margin: "0 0 6px", fontFamily: "Georgia, serif" }}>
                            {desc}
                          </p>
                          {shared.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 8 }}>
                              <div style={{ fontSize: "0.6rem", letterSpacing: "0.08em", textTransform: "uppercase", color: textDim }}>Geteilte Begriffe</div>
                              {shared.map(({ term, defA, defB }) => (
                                <div key={term} style={{ background: surface, borderRadius: 6, padding: "7px 9px" }}>
                                  <div style={{ fontSize: "0.65rem", fontWeight: 600, color: accent, marginBottom: 5 }}>{term}</div>
                                  <div style={{ fontSize: "0.68rem", color: textMuted, lineHeight: 1.55, fontFamily: "Georgia, serif", marginBottom: 3 }}>
                                    <span style={{ color: textDim }}>{od ? od.label : otherId}:</span> {defB}
                                  </div>
                                  <div style={{ fontSize: "0.68rem", color: textMuted, lineHeight: 1.55, fontFamily: "Georgia, serif" }}>
                                    <span style={{ color: textDim }}>{ALL_DATA[focused] ? ALL_DATA[focused].label : focused}:</span> {defA}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

        </div>
      );
    }

    // ─── LandkartePanel ───────────────────────────────────────────────────────
    function LandkartePanel({ onSelect }) {
      const mob = useIsMobile();
      const [hovered, setHovered] = React.useState(null);

      const DR = 7, DS = 36, GG = 32, P = 16, LH = 18;

      function dotPos(gi, ti) {
        if (mob) return {
          x: P + (ti % 5) * DS + DR,
          y: P + LH + gi * (2 * DS + GG) + Math.floor(ti / 5) * DS + DR
        };
        return {
          x: P + gi * (2 * DS + GG) + (ti % 2) * DS + DR,
          y: P + LH + Math.floor(ti / 2) * DS + DR
        };
      }

      function labelPos(gi) {
        if (mob) return { x: P + 2.5 * DS, y: P + LH + gi * (2 * DS + GG) - 5 };
        return { x: P + gi * (2 * DS + GG) + DS - DR, y: P + LH - 5 };
      }

      const svgW = mob ? P * 2 + 5 * DS : P * 2 + 5 * (2 * DS + GG) - GG;
      const svgH = mob ? P * 2 + 5 * (LH + 2 * DS + GG) - GG : P * 2 + LH + 5 * DS;

      const idPos = {};
      TOPIC_GROUPS.forEach((group, gi) => {
        group.ids.forEach((id, ti) => { idPos[id] = dotPos(gi, ti); });
      });

      const CROSS_LINKS = [
        ["watzlawick", "bateson"],
        ["bateson",    "argyris"],
        ["glasersfeld","watzlawick"],
        ["watzlawick", "piaget"],
        ["foerster",   "luhmann"],
        ["luhmann",    "spencerbrown"],
        ["spencerbrown","bateson"],
        ["bateson",    "goffman"],
      ];

      const SHORT = ["Systeme", "Erkenntnis", "Sprache", "Psyche", "Wirkung"];

      return (
        <div style={{ overflowX: "auto" }}>
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            style={{ width: "100%", maxWidth: mob ? "100%" : svgW, display: "block" }}
            aria-label="Landkarte aller 50 Denker*innen"
          >
            {/* Querverbindungen */}
            {CROSS_LINKS.map(([a, b]) => {
              const pa = idPos[a], pb = idPos[b];
              if (!pa || !pb) return null;
              return (
                <line key={a + b}
                  x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y}
                  stroke={border} strokeWidth={1} opacity={0.6}
                />
              );
            })}

            {/* Gruppen-Labels */}
            {TOPIC_GROUPS.map((group, gi) => {
              const lp = labelPos(gi);
              return (
                <text key={group.id + "L"} x={lp.x} y={lp.y}
                  textAnchor="middle" fontSize="7" fill={textDim}
                  fontFamily="'Raleway', system-ui, sans-serif" letterSpacing="0.3">
                  {SHORT[gi]}
                </text>
              );
            })}

            {/* Dots */}
            {TOPIC_GROUPS.map((group, gi) =>
              group.ids.map((id, ti) => {
                const prog = getThinkerProgress(id);
                const { x, y } = dotPos(gi, ti);
                const fillColor   = prog === "vertieft"   ? accent    : prog === "angefangen" ? accentDim : surface;
                const strokeColor = prog === "unberuehrt" ? border     : accent;
                return (
                  <circle key={id} cx={x} cy={y} r={DR}
                    fill={fillColor} stroke={strokeColor} strokeWidth={1.5}
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelect(id)}
                    onMouseEnter={() => setHovered(id)}
                    onMouseLeave={() => setHovered(null)}
                  />
                );
              })
            )}

            {/* Tooltip */}
            {hovered && idPos[hovered] && (() => {
              const { x, y } = idPos[hovered];
              const d = ALL_DATA[hovered];
              const prog = getThinkerProgress(hovered);
              const label = d ? d.label : hovered;
              const progLabel = prog === "vertieft" ? "Vertieft" : prog === "angefangen" ? "Angefangen" : "Noch nicht erkundet";
              const tw = Math.max(label.length, progLabel.length) * 4.5 + 12;
              const tx = Math.min(Math.max(x, tw / 2 + 4), svgW - tw / 2 - 4);
              const ty = Math.max(y - DR - 20, 14);
              return (
                <g>
                  <rect x={tx - tw / 2} y={ty - 10} width={tw} height={22}
                    rx={4} fill={surface2} stroke={border} strokeWidth={0.8} />
                  <text x={tx} y={ty + 1} textAnchor="middle" fontSize="7"
                    fill="#e8e6e3" fontFamily="'Raleway', system-ui, sans-serif" fontWeight="600">
                    {label}
                  </text>
                  <text x={tx} y={ty + 10} textAnchor="middle" fontSize="6"
                    fill={textDim} fontFamily="'Raleway', system-ui, sans-serif">
                    {progLabel}
                  </text>
                </g>
              );
            })()}

          </svg>
        </div>
      );
    }

    // ─── LernpfadePanel ───────────────────────────────────────────────────────
    function LernpfadePanel({ onSelectWithMode }) {
      const [customPfade, setCustomPfade] = usePersistentState("global.pfade", []);
      const [showCreator, setShowCreator] = React.useState(false);
      const [creatorTitle, setCreatorTitle] = React.useState("");
      const [creatorSelected, setCreatorSelected] = React.useState([]);

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* ── Kuratierte Pfade ── */}
          {LEARNING_PATHS.map(path => {
            const started = path.stations.filter(s => getThinkerProgress(s.id) !== "unberuehrt").length;
            const total = path.stations.length;
            const pct = (started / total) * 100;
            const nextStation = path.stations.find(s => getThinkerProgress(s.id) === "unberuehrt") || path.stations[0];
            const ctaLabel = started === 0 ? "Pfad starten" : started === total ? "Wiederholen" : "Weiter";
            return (
              <div key={path.id} style={{ background: surface, borderRadius: 10, border: "1px solid " + border, padding: "16px 18px" }}>
                {path.onboarding && (
                  <div style={{ display: "inline-block", fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: accent, border: "1px solid " + accentBorder, borderRadius: 4, padding: "2px 7px", marginBottom: 10 }}>Einstieg</div>
                )}
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#e8e6e3", marginBottom: 4 }}>{path.title}</div>
                <div style={{ fontSize: "0.72rem", color: textMuted, marginBottom: 12, fontStyle: "italic", fontFamily: "Georgia, serif" }}>{path.leitfrage}</div>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
                  {path.stations.map((s, i) => {
                    const done = getThinkerProgress(s.id) !== "unberuehrt";
                    const d = ALL_DATA[s.id];
                    return (
                      <React.Fragment key={s.id + i}>
                        <span style={{ fontSize: "0.65rem", padding: "2px 8px", borderRadius: 12, background: done ? accentDim : "transparent", color: done ? accent : textDim, border: "1px solid " + (done ? accentBorder : borderLight) }}>{d ? d.label : s.id}</span>
                        {i < path.stations.length - 1 && <span style={{ color: textDim, fontSize: "0.65rem" }}>→</span>}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 3, borderRadius: 2, background: border, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: pct === 100 ? accent : accentDim, borderRadius: 2, transition: "width 0.3s ease" }} />
                    </div>
                    <div style={{ fontSize: "0.62rem", color: textDim, marginTop: 5 }}>{started} von {total} erkundet</div>
                  </div>
                  <button onClick={() => onSelectWithMode(nextStation.id, nextStation.mode)}
                    style={{ background: accent, border: "none", borderRadius: 7, padding: "7px 14px", color: bg, fontSize: "0.7rem", fontWeight: 600, fontFamily: "inherit", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#d4584c"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = accent; }}>
                    {ctaLabel}
                  </button>
                </div>
              </div>
            );
          })}

          {/* ── Trennlinie ── */}
          <div style={{ borderTop: "1px solid " + border, marginTop: 4 }} />

          {/* ── Eigene Pfade ── */}
          {customPfade.map(path => {
            const started = path.stations.filter(s => getThinkerProgress(s.id) !== "unberuehrt").length;
            const total = path.stations.length;
            const pct = total > 0 ? (started / total) * 100 : 0;
            const nextStation = path.stations.find(s => getThinkerProgress(s.id) === "unberuehrt") || path.stations[0];
            const ctaLabel = started === 0 ? "Pfad starten" : started === total ? "Wiederholen" : "Weiter";
            return (
              <div key={path.id} style={{ background: surface, borderRadius: 10, border: "1px solid " + border, padding: "16px 18px", position: "relative" }}>
                <button onClick={() => setCustomPfade(prev => prev.filter(p => p.id !== path.id))}
                  aria-label="Pfad loeschen"
                  style={{ position: "absolute", top: 10, right: 12, background: "none", border: "none", cursor: "pointer", color: textDim, fontSize: "0.8rem", padding: 4, lineHeight: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.color = danger; }}
                  onMouseLeave={e => { e.currentTarget.style.color = textDim; }}>✕</button>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#e8e6e3", marginBottom: 12, paddingRight: 24 }}>{path.title}</div>
                {path.stations.length > 0 && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
                      {path.stations.map((s, i) => {
                        const done = getThinkerProgress(s.id) !== "unberuehrt";
                        const d = ALL_DATA[s.id];
                        return (
                          <React.Fragment key={s.id + i}>
                            <span style={{ fontSize: "0.65rem", padding: "2px 8px", borderRadius: 12, background: done ? accentDim : "transparent", color: done ? accent : textDim, border: "1px solid " + (done ? accentBorder : borderLight) }}>{d ? d.label : s.id}</span>
                            {i < path.stations.length - 1 && <span style={{ color: textDim, fontSize: "0.65rem" }}>→</span>}
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ height: 3, borderRadius: 2, background: border, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: pct + "%", background: pct === 100 ? accent : accentDim, borderRadius: 2, transition: "width 0.3s ease" }} />
                        </div>
                        <div style={{ fontSize: "0.62rem", color: textDim, marginTop: 5 }}>{started} von {total} erkundet</div>
                      </div>
                      <button onClick={() => onSelectWithMode(nextStation.id, nextStation.mode)}
                        style={{ background: accent, border: "none", borderRadius: 7, padding: "7px 14px", color: bg, fontSize: "0.7rem", fontWeight: 600, fontFamily: "inherit", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#d4584c"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = accent; }}>
                        {ctaLabel}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* ── + Button ── */}
          <button onClick={() => setShowCreator(true)}
            style={{ background: "transparent", border: "1px dashed " + border, borderRadius: 10, padding: "12px 18px", color: textDim, fontSize: "0.75rem", fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = accentBorder; e.currentTarget.style.color = "#e8ede9"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textDim; }}>
            + Eigenen Pfad erstellen
          </button>

          {/* ── Creator Overlay ── */}
          {showCreator && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(38,39,41,0.92)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 18px", overflowY: "auto" }}>
              <div style={{ background: surface2, borderRadius: 12, border: "1px solid " + border, width: "100%", maxWidth: 540, padding: "22px 20px" }}>

                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                  <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#e8e6e3" }}>Eigenen Pfad erstellen</span>
                  <button onClick={() => { setShowCreator(false); setCreatorTitle(""); setCreatorSelected([]); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: textDim, fontSize: "1rem", padding: 4, lineHeight: 1 }}>✕</button>
                </div>

                {/* Titel */}
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: textDim, marginBottom: 6 }}>Titel</div>
                  <input
                    value={creatorTitle}
                    onChange={e => setCreatorTitle(e.target.value)}
                    placeholder="Mein Pfad"
                    style={{ width: "100%", background: bg, border: "1px solid " + border, borderRadius: 7, padding: "8px 12px", color: "#e8e6e3", fontFamily: "inherit", fontSize: "0.85rem", outline: "none", boxSizing: "border-box" }}
                    onFocus={e => { e.currentTarget.style.borderColor = accentBorder; }}
                    onBlur={e => { e.currentTarget.style.borderColor = border; }}
                  />
                </div>

                {/* Ausgewählte Denker */}
                {creatorSelected.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: textDim, marginBottom: 6 }}>Ausgewaehlt ({creatorSelected.length})</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {creatorSelected.map((id, i) => {
                        const d = ALL_DATA[id];
                        return (
                          <React.Fragment key={id + i}>
                            <button onClick={() => setCreatorSelected(prev => prev.filter(x => x !== id))}
                              style={{ fontSize: "0.65rem", padding: "2px 8px", borderRadius: 12, background: accentDim, color: accent, border: "1px solid " + accentBorder, fontFamily: "inherit", cursor: "pointer" }}>
                              {d ? d.label : id} ✕
                            </button>
                            {i < creatorSelected.length - 1 && <span style={{ color: textDim, fontSize: "0.65rem", alignSelf: "center" }}>→</span>}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Denker-Picker */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: "0.65rem", letterSpacing: "0.1em", textTransform: "uppercase", color: textDim, marginBottom: 10 }}>Denker*innen auswaehlen</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {TOPIC_GROUPS.map(group => (
                      <div key={group.id}>
                        <div style={{ fontSize: "0.6rem", color: textDim, marginBottom: 5, letterSpacing: "0.05em" }}>{group.label}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {group.ids.map(id => {
                            const d = ALL_DATA[id];
                            const sel = creatorSelected.includes(id);
                            return (
                              <button key={id}
                                onClick={() => setCreatorSelected(prev => sel ? prev.filter(x => x !== id) : [...prev, id])}
                                style={{ fontSize: "0.65rem", padding: "2px 8px", borderRadius: 12, background: sel ? accentDim : "transparent", color: sel ? accent : textDim, border: "1px solid " + (sel ? accentBorder : borderLight), fontFamily: "inherit", cursor: "pointer", fontWeight: sel ? 600 : 400 }}>
                                {d ? d.label : id}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={() => { setShowCreator(false); setCreatorTitle(""); setCreatorSelected([]); }}
                    style={{ background: "transparent", border: "1px solid " + border, borderRadius: 7, padding: "7px 16px", color: textMuted, fontSize: "0.75rem", fontFamily: "inherit", cursor: "pointer" }}>
                    Abbrechen
                  </button>
                  <button
                    onClick={() => {
                      if (!creatorTitle.trim() || creatorSelected.length < 1) return;
                      const newPfad = { id: "custom_" + Date.now(), title: creatorTitle.trim(), stations: creatorSelected.map(id => ({ id, mode: "entdecken" })) };
                      setCustomPfade(prev => [...prev, newPfad]);
                      setShowCreator(false);
                      setCreatorTitle("");
                      setCreatorSelected([]);
                    }}
                    disabled={!creatorTitle.trim() || creatorSelected.length < 1}
                    style={{ background: (!creatorTitle.trim() || creatorSelected.length < 1) ? border : accent, border: "none", borderRadius: 7, padding: "7px 16px", color: (!creatorTitle.trim() || creatorSelected.length < 1) ? textDim : bg, fontSize: "0.75rem", fontWeight: 600, fontFamily: "inherit", cursor: (!creatorTitle.trim() || creatorSelected.length < 1) ? "not-allowed" : "pointer" }}>
                    Pfad speichern
                  </button>
                </div>

              </div>
            </div>
          )}

        </div>
      );
    }

    // ─── MeinBereichView ──────────────────────────────────────────────────────
    function MeinBereichView({ onBack, userName, onNameChange, onSelect, onSelectWithMode, initialTab }) {
      const [editing, setEditing] = React.useState(false);
      const [draft, setDraft] = React.useState(userName);
      const [meinMode, setMeinMode] = React.useState(initialTab || "erkundung");
      const mob = useIsMobile();

      function commitName() {
        const trimmed = draft.trim();
        onNameChange(trimmed);
        setEditing(false);
      }

      const tabs = [
        { id: "erkundung",  label: "01 Erkundungsreise", intro: "Alle Konzepte auf einen Blick, geordnet nach den fünf Kategorien — mit deinem persönlichen Fortschritt." },
        { id: "lernpfade",  label: "02 Lernpfade",       intro: "Pfade durch die Konzepte, kuratiert entlang von Fragen — oder selbst zusammengestellt." },
        { id: "landkarte",  label: "03 Landkarte",        intro: "Das Netz der Verbindungen zwischen allen 50 Denker*innen — klickbar und erkundbar." },
      ];

      return (
        <div style={{ background: bg, minHeight: "100vh", color: "#e8ede9", fontFamily: "'Raleway', system-ui, sans-serif" }}>

          {/* Name-Header */}
          <div style={{ padding: mob ? "16px 18px 18px" : "18px 24px 20px", borderBottom: "1px solid " + border }}>
            {editing ? (
              <input
                autoFocus
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={commitName}
                onKeyDown={e => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setDraft(userName); setEditing(false); } }}
                placeholder="Dein Name"
                style={{ background: "transparent", border: "none", borderBottom: "1px solid " + accent, color: "#e8e6e3", fontFamily: "'Raleway', system-ui, sans-serif", fontSize: mob ? "0.95rem" : "1.0rem", fontWeight: 700, outline: "none", width: "100%", padding: "2px 0" }}
              />
            ) : (
              <button
                onClick={() => { setDraft(userName); setEditing(true); }}
                aria-label="Namen bearbeiten"
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10 }}
              >
                <h1 style={{ fontFamily: "'Raleway', system-ui, sans-serif", fontSize: mob ? "0.95rem" : "1.0rem", fontWeight: 700, color: "#e8e6e3", margin: 0, letterSpacing: "-0.02em" }}>
                  {userName || "Dein Name"}
                </h1>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M11 2l3 3-9 9H2v-3L11 2z" stroke={textDim} strokeWidth="1.4" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            <p style={{ fontSize: "0.78rem", color: textDim, margin: "6px 0 0", lineHeight: 1.5 }}>
              Dein Raum — Fortschritt, Pfade und Verbindungen auf einen Blick.
            </p>
            <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
              <button onClick={() => {
                const data = {};
                Object.keys(localStorage).filter(k => k.startsWith("secondorder.v1.")).forEach(k => { data[k] = localStorage.getItem(k); });
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = "secondorder-backup.json"; a.click();
                URL.revokeObjectURL(url);
              }} style={{ background: "none", border: "1px solid " + border, borderRadius: 100, cursor: "pointer", fontSize: "0.65rem", color: textMuted, padding: "3px 10px", fontFamily: "inherit" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = borderLight; e.currentTarget.style.color = "#e8ede9"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}>
                Export
              </button>
              <label style={{ fontSize: "0.65rem", color: textMuted, cursor: "pointer", border: "1px solid " + border, borderRadius: 100, padding: "3px 10px", display: "inline-block" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = borderLight; e.currentTarget.style.color = "#e8ede9"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}>
                Import
                <input type="file" accept=".json" style={{ display: "none" }} onChange={e => {
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = ev => {
                    try {
                      const data = JSON.parse(ev.target.result);
                      if (typeof data !== "object" || Array.isArray(data)) throw new Error("invalid");
                      const validEntries = Object.entries(data).filter(([k, v]) => k.startsWith("secondorder.v1.") && typeof v === "string");
                      if (validEntries.length === 0) throw new Error("no valid keys");
                      if (validEntries.length > 1000) throw new Error("too many keys");
                      validEntries.forEach(([k, v]) => localStorage.setItem(k, v));
                      window.location.reload();
                    } catch { alert("Import fehlgeschlagen — ungültige oder leere Datei."); }
                  };
                  reader.readAsText(file);
                }} />
              </label>
            </div>
          </div>

          {/* Tab-Leiste */}
          <div style={{ display: "flex", borderBottom: "1px solid " + border, padding: "0 18px" }}>
            {tabs.map(t => {
              const active = meinMode === t.id;
              return (
                <button key={t.id} onClick={() => setMeinMode(t.id)}
                  style={{ background: "none", border: "none", padding: "10px 13px", color: active ? accent : textDim, fontSize: "0.7rem", cursor: "pointer", letterSpacing: "0.03em" }}>
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Tab-Inhalt */}
          <div style={{ padding: meinMode === "landkarte" ? "28px 18px" : "28px 18px" }}>
            {meinMode === "landkarte" ? (
              <>
                {tabs.find(t => t.id === meinMode)?.intro && (
                  <p style={{ fontSize: "0.75rem", color: textDim, fontFamily: "Georgia, serif", margin: "0 0 20px", lineHeight: 1.7, maxWidth: 680 }}>
                    {tabs.find(t => t.id === meinMode).intro}
                  </p>
                )}
                <GraphPanel onSelect={onSelect} />
              </>
            ) : (
              <div style={{ maxWidth: 680, margin: "0 auto" }}>
                {tabs.find(t => t.id === meinMode)?.intro && (
                  <p style={{ fontSize: "0.75rem", color: textDim, fontFamily: "Georgia, serif", margin: "0 0 20px", lineHeight: 1.7 }}>
                    {tabs.find(t => t.id === meinMode).intro}
                  </p>
                )}
                {meinMode === "erkundung" ? (
                  <ErkundungsPanel onSelect={onSelect} />
                ) : (
                  <LernpfadePanel onSelectWithMode={onSelectWithMode} />
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // ─── PWA Install-Banner ───────────────────────────────────────────────────
    function InstallBanner() {
      const [prompt, setPrompt] = React.useState(null);
      const [visible, setVisible] = React.useState(false);

      React.useEffect(() => {
        if (localStorage.getItem(STORAGE_PREFIX + "global.installDismissed")) return;
        if (window.matchMedia("(display-mode: standalone)").matches) return;
        function onBefore(e) {
          e.preventDefault();
          setPrompt(e);
          setTimeout(() => setVisible(true), 3000);
        }
        window.addEventListener("beforeinstallprompt", onBefore);
        return () => window.removeEventListener("beforeinstallprompt", onBefore);
      }, []);

      if (!visible || !prompt) return null;

      async function install() {
        prompt.prompt();
        await prompt.userChoice;
        localStorage.setItem(STORAGE_PREFIX + "global.installDismissed", "1");
        setVisible(false);
      }
      function dismiss() {
        localStorage.setItem(STORAGE_PREFIX + "global.installDismissed", "1");
        setVisible(false);
      }

      return (
        <div style={{ position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", zIndex: 300, background: surface2, border: "1px solid " + borderLight, borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.4)", maxWidth: "calc(100vw - 36px)", fontFamily: "'Raleway', system-ui, sans-serif" }}>
          <span style={{ fontSize: "0.75rem", color: "#e8e6e3", whiteSpace: "nowrap" }}>Zur Startseite hinzufügen</span>
          <button onClick={install}
            style={{ background: accent, border: "none", borderRadius: 7, padding: "6px 14px", color: bg, fontSize: "0.72rem", fontWeight: 600, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
            Installieren
          </button>
          <button onClick={dismiss}
            style={{ background: "none", border: "none", cursor: "pointer", color: textDim, fontSize: "0.9rem", padding: 2, lineHeight: 1, flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = "#e8e6e3"}
            onMouseLeave={e => e.currentTarget.style.color = textDim}>✕</button>
        </div>
      );
    }

    // ─── MilestoneToast ───────────────────────────────────────────────────────
    const MILESTONES = {
      1:  "1 von 50 — Jedem Anfang wohnt ein Zauber inne.",
      5:  "5 von 50 — Erste Verbindungen entstehen.",
      10: "10 von 50 — Die Landschaft beginnt, Konturen zu zeigen.",
      15: "15 von 50 — Manches, was du am Anfang dachtest, denkst du jetzt anders.",
      30: "30 von 50 — Der Blick hat sich mit dir bewegt.",
      45: "45 von 50 — Was hat sich verändert — in den Ideen, oder in dir?",
      50: "50 von 50 — Was siehst du jetzt, was du am Anfang nicht sehen konntest?",
    };

    function MilestoneToast({ text, onClose }) {
      React.useEffect(() => {
        const t = setTimeout(onClose, 5000);
        return () => clearTimeout(t);
      }, []);
      return (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 400, background: surface2, border: "1px solid " + borderLight, borderRadius: 12, padding: "12px 18px", display: "flex", alignItems: "center", gap: 14, boxShadow: "0 4px 28px rgba(0,0,0,0.45)", maxWidth: "calc(100vw - 36px)", fontFamily: "'Raleway', system-ui, sans-serif", animation: "fadeIn 0.3s ease" }}>
          <span style={{ fontSize: "0.78rem", color: "#e8e6e3", fontFamily: "Georgia, serif", fontStyle: "italic", lineHeight: 1.5 }}>{text}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: textDim, fontSize: "0.85rem", padding: 2, lineHeight: 1, flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = "#e8e6e3"}
            onMouseLeave={e => e.currentTarget.style.color = textDim}>✕</button>
        </div>
      );
    }

    // ─── Hash-Routing (parseHash/buildHash) -> src/routing.js ──────────────────

    // ─── Haupt-App ────────────────────────────────────────────────────────────
    function App() {
      const initial = parseHash();
      const [view, setView] = React.useState(initial.view);
      const [topicId, setTopicId] = React.useState(initial.topicId);
      const [mode, setMode] = React.useState(initial.mode);
      const [meinTab, setMeinTab] = React.useState(initial.meinTab);
      const [searchOpen, setSearchOpen] = React.useState(false);
      const [scrollTarget, setScrollTarget] = React.useState(null);
      const [userName, setUserName] = usePersistentState("global.userName", "");
      const searchIndexRef = React.useRef(null);
      const searchIndex = React.useMemo(() => {
        if (!searchIndexRef.current) searchIndexRef.current = buildSearchIndex();
        return searchIndexRef.current;
      }, [searchOpen]);
      const isMobile = useIsMobile();

      function navigate(newView, opts = {}) {
        const newTopicId = opts.topicId !== undefined ? opts.topicId : topicId;
        const newMode = opts.mode !== undefined ? opts.mode : "entdecken";
        const newMeinTab = opts.meinTab !== undefined ? opts.meinTab : meinTab;
        if (opts.topicId !== undefined) setTopicId(opts.topicId);
        if (opts.mode !== undefined) setMode(opts.mode);
        if (opts.meinTab !== undefined) setMeinTab(opts.meinTab);
        setView(newView);
        window.location.hash = buildHash(newView, newTopicId, newMode).slice(1);
      }

      const [milestoneText, setMilestoneText] = React.useState(null);

      function checkMilestone(newId) {
        const isNew = getThinkerProgress(newId) === "unberuehrt";
        const count = TOPIC_ORDER.filter(id => getThinkerProgress(id) !== "unberuehrt").length;
        const effectiveCount = isNew ? count + 1 : count;
        const achieved = JSON.parse(localStorage.getItem(STORAGE_PREFIX + "global.milestones") || "[]");
        const threshold = [1, 5, 10, 15, 30, 45, 50].find(t => effectiveCount >= t && !achieved.includes(t));
        if (threshold) {
          localStorage.setItem(STORAGE_PREFIX + "global.milestones", JSON.stringify([...achieved, threshold]));
          setMilestoneText(MILESTONES[threshold]);
        }
      }

      function selectThinker(id) { localStorage.setItem(STORAGE_PREFIX + "global.lastVisited", id); checkMilestone(id); navigate("thinker", { topicId: id, mode: "entdecken" }); }
      function selectThinkerWithMode(id, m) { localStorage.setItem(STORAGE_PREFIX + "global.lastVisited", id); checkMilestone(id); navigate("thinker", { topicId: id, mode: m || "entdecken" }); }
      function selectGroup(g) { selectThinker(g.ids[0]); }

      React.useEffect(() => {
        function onHash() {
          const s = parseHash();
          setView(s.view); setTopicId(s.topicId); setMode(s.mode); setMeinTab(s.meinTab);
        }
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
      }, []);

      React.useEffect(() => {
        function onKey(e) {
          if (e.key === "/" && e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
            e.preventDefault();
            if (view === "thinker") setSearchOpen(true);
          }
          if (e.key === "Escape") setSearchOpen(false);
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
      }, [view]);

      const topic = TOPICS.find(t => t.id === topicId);
      const data = ALL_DATA[topicId] || null;

      const tabs = [
        { id: "entdecken", label: "01 Entdecken" },
        { id: "vernetzen", label: "02 Verbinden" },
        { id: "anwenden",  label: "03 Anwenden"  },
      ];

      return (
        <div style={{ background: bg, minHeight: "100vh", color: "#e8ede9", fontFamily: "'Raleway', system-ui, sans-serif" }}>

          {/* Skip-Link */}
          <a href="#main-content"
            style={{ position: "absolute", left: "-9999px", top: 4, zIndex: 9999, padding: "8px 16px", background: accent, color: bg, fontSize: "0.8rem", borderRadius: 4, textDecoration: "none", fontFamily: "inherit" }}
            onFocus={e => { e.currentTarget.style.left = "4px"; }}
            onBlur={e => { e.currentTarget.style.left = "-9999px"; }}>
            Zum Inhalt springen
          </a>

          {/* Header — nur bei thinker/about */}
          {view !== "landing" && (
            <header style={{ padding: "15px 18px", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid " + border, background: "rgba(38,39,41,0.97)", position: "sticky", top: 0, zIndex: 100 }}>
              <button onClick={() => navigate("landing")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="95 85 210 210" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <circle cx="200" cy="192" r="96" stroke={accent} strokeWidth="2" opacity="0.38" fill="none"/>
                  <path d="M200,110 C112,110 112,192 200,192" stroke={accent} strokeWidth="9.5" fill="none" strokeLinecap="round"/>
                  <path d="M200,192 C288,192 288,274 200,274" stroke={accent} strokeWidth="9.5" fill="none" strokeLinecap="round"/>
                  <circle cx="200" cy="110" r="7" fill={accent}/>
                  <circle cx="200" cy="274" r="7" fill={accent}/>
                  <circle cx="200" cy="192" r="9" fill={bg}/>
                  <circle cx="200" cy="192" r="9" stroke={accent} strokeWidth="2.5" fill="none"/>
                  <circle cx="200" cy="192" r="4" fill={accent}/>
                </svg>
                <span style={{ fontWeight: 800, fontSize: "0.95rem", letterSpacing: "-0.02em", color: "#e8e6e3", whiteSpace: "nowrap" }}>Second Order</span>
              </button>
              {!isMobile && <span style={{ fontSize: "0.65rem", color: textDim, marginLeft: 4, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>Konzepte, die einen Unterschied machen</span>}
              {isMobile && <span style={{ flex: 1 }} />}
              {view === "thinker" && (
                <button
                  onClick={() => setSearchOpen(true)}
                  aria-label="Suche öffnen"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: textDim, display: "flex", alignItems: "center", flexShrink: 0 }}
                >
                  <svg width="17" height="17" viewBox="0 0 16 16" fill="none">
                    <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
                    <line x1="10" y1="10" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
              <button
                onClick={() => navigate("mein")}
                aria-label="Meine Erkundungsreise"
                style={{ background: userName.trim() ? accentDim : "rgba(255,255,255,0.06)", border: "1px solid " + (userName.trim() ? accentBorder : border), borderRadius: "50%", width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, padding: 0 }}
              >
                {userName.trim() ? (
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, color: accent, lineHeight: 1 }}>
                    {userName.trim()[0].toUpperCase()}
                  </span>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="5.5" r="2.5" stroke={textDim} strokeWidth="1.4"/>
                    <path d="M2.5 13.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke={textDim} strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                )}
              </button>
            </header>
          )}

          {view === "thinker" && (
            <GroupNav topicId={topicId} onSelect={selectThinker} />
          )}

          <main id="main-content">
          {view === "landing" ? (
            <LandingPage onSelectGroup={selectGroup} onAbout={() => navigate("about")} onMein={() => navigate("mein")} userName={userName} />
          ) : view === "about" ? (
            <AboutView onBack={() => navigate("landing")} onSelect={selectThinker} onSelectWithMode={selectThinkerWithMode} onMein={(tab) => navigate("mein", { meinTab: tab })} />
          ) : view === "mein" ? (
            <MeinBereichView key={meinTab} onBack={() => navigate("landing")} userName={userName} onNameChange={setUserName} onSelect={selectThinker} onSelectWithMode={selectThinkerWithMode} initialTab={meinTab} />
          ) : (
            <>
              {/* Hero */}
              <div style={{ padding: "26px 18px 14px" }}>
                <div style={{ fontSize: "0.65rem", letterSpacing: "0.12em", textTransform: "uppercase", color: accent, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ display: "inline-block", width: 14, height: 1, background: accent }} />
                  {topic.subtitle}
                </div>
                <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.7rem,5vw,2.8rem)", fontWeight: 400, lineHeight: 1.1, letterSpacing: "-0.02em", marginBottom: 0 }}>
                  {topic.title} <em style={{ fontStyle: "italic", color: "#f5a99e" }}>{topic.em}</em>
                </h1>
                {data && data.quote && (
                  <p style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "0.92rem", lineHeight: 1.65, color: textMuted, marginTop: 16, maxWidth: 620, marginBottom: 0 }}>
                    <span style={{ color: accent, fontSize: "1.4rem", lineHeight: 0.4, verticalAlign: "-0.18em", marginRight: 4 }}>"</span>{data.quote}
                  </p>
                )}
                {data && data.desc && (
                  <p style={{ fontFamily: "Georgia, serif", fontSize: "0.95rem", lineHeight: 1.55, color: "#c8cbcd", marginTop: 14, maxWidth: 620 }}>
                    {data.desc}
                  </p>
                )}
                {data && <ThinkerNote topicId={topicId} />}
              </div>

              {/* Mode tabs */}
              <div style={{ display: "flex", borderBottom: "1px solid " + border, padding: "0 18px" }}>
                {tabs.map(t => {
                  const active = mode === t.id;
                  return (
                    <button key={t.id} onClick={() => { setMode(t.id); window.location.hash = buildHash("thinker", topicId, t.id).slice(1); }}
                      style={{ background: "none", border: "none", padding: "10px 13px", color: active ? accent : textDim, fontSize: "0.7rem", cursor: "pointer", letterSpacing: "0.03em" }}>
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Content */}
              <div style={{ padding: "22px 18px" }}>
                {!data ? (
                  <div style={{ padding: "40px 20px", textAlign: "center" }}>
                    <div style={{ fontSize: "0.72rem", color: textDim, lineHeight: 1.8 }}>
                      Keine Inhalte für <span style={{ color: accent }}>{topic.label}</span> gefunden.
                    </div>
                  </div>
                ) : (
                  <>
                    {mode === "entdecken" && <EntdeckenPanel key={topicId + "-e"} data={data} scrollToTerm={scrollTarget} />}
                    {mode === "vernetzen" && <VernetzenPanel key={topicId + "-v"} data={data} onGoAnwenden={() => setMode("anwenden")} />}
                    {mode === "anwenden"  && <AnwendenPanel  key={topicId + "-a"} data={data} topicId={topicId} onSelectWithMode={selectThinkerWithMode} />}
                  </>
                )}
              </div>
            </>
          )}
          </main>

          {searchOpen && (
            <SearchOverlay
              index={searchIndex}
              onSelectThinker={id => selectThinker(id)}
              onSelectTerm={(id, term) => { setTopicId(id); setMode("entdecken"); setView("thinker"); setScrollTarget(term ? { term, nonce: Date.now() } : null); }}
              onClose={() => setSearchOpen(false)}
            />
          )}
          <InstallBanner />
          {milestoneText && <MilestoneToast text={milestoneText} onClose={() => setMilestoneText(null)} />}
        </div>
      );
    }

    ReactDOM.createRoot(document.getElementById("root")).render(<App />);
  